import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import * as rn from 'react-native'

import * as c from '../common'
import { hasAnySignature } from '../common/helpers/signingFlowV2'

// import axios from 'axios'
import moment from 'moment'
import { Provider } from 'react-redux'

export const log = (obj: c.ReadonlyRecord<unknown>): void => {
  const deps = c.values(obj)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    console.log(obj)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

export const useIsMounted = () => {
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  return useCallback(() => isMounted.current, [])
}

export const useBool = (initialState: boolean) => {
  const [currState, setState] = useState<boolean>(initialState)

  const toggle = useCallback((): void => {
    setState((curr) => !curr)
  }, [])

  return [currState, setState, toggle] as const
}

export const useInputVal = (initVal = '') => {
  const [val, setVal] = React.useState<string>(initVal)
  const handleClean = React.useCallback(() => void setVal(''), [])
  const handleInputChange = React.useCallback(
    (e: { target: { value: string } }): void => {
      setVal(e.target.value)
    },
    [],
  )

  return [val, setVal, handleInputChange, handleClean] as const
}

export const useWhatChanged = (deps: unknown[], name: string): void => {
  const refs = deps.map(
    // Hooks can be called inside map() if the array doesn't change length.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    (val): React.MutableRefObject<unknown> => useRef<unknown>(val),
  )

  useEffect((): void => {
    for (let i = 0; i < deps.length; i++) {
      const dep = deps[i]

      if (dep !== refs[i]!.current) {
        c.log(`${name} -> dep changed at index ${i}`)
        refs[i]!.current = dep
      }
    }
    // TODO: Look at why deps are specified this way
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

export const useForceUpdate = () => {
  const [, setTimestamp] = useState(Date.now())

  return useCallback(() => {
    setTimestamp(Date.now())
  }, [])
}

const tbdInstallerOpt: c.Opts = [
  { label: 'To Be Decided', value: 'To Be Decided' },
]
export const useInstallers = (solarCompany: string) => {
  const stateToInstallers = useEnv<c.DBValues>('installers') || c.emptyObj
  const [candidate, setCandidate] = React.useState('To Be Decided')

  const state = c.getCompanyState(solarCompany)

  const opts = React.useMemo((): c.Opts => {
    const dbOpts = Object.keys(
      (stateToInstallers || {})[state || ''] || c.emptyObj,
    ).map((i) => ({
      label: i,
      value: i,
    }))

    if (dbOpts.length === 0) {
      return tbdInstallerOpt
    }

    return tbdInstallerOpt.concat(dbOpts)
  }, [state, stateToInstallers])

  return [opts, candidate, setCandidate] as const
}

export const useBattery = (customerID: string) => {
  const [solarCompany] = useCustomerField(customerID, 'solarCompany')
  const [installers] = useInstallers(solarCompany)
  const [installer] = useCustomerField(
    customerID,
    'main_panel_upgrade_installation_company',
  )
  const batteryTypeStr = useEnv<string>('batteryType') || c.emptyStrArr
  const allBatterySizes =
    useEnv<Record<string, string>>('batterySize') || c.emptyObj
  const [customerBatteryType] = useCustomerField(customerID, 'battery_type')

  const batterySize = React.useMemo(() => {
    if (
      !customerBatteryType ||
      !customerBatteryType ||
      !installer ||
      !allBatterySizes
    ) {
      return c.EMPTY_ARRAY
    }

    const key = `Battery ${customerBatteryType}-${installer}`
    return allBatterySizes[key]
  }, [allBatterySizes, installer, customerBatteryType])

  const batteryTypes = React.useMemo((): c.Opts => {
    const allOptions = c.convertToOptions(batteryTypeStr)
    return allOptions
  }, [batteryTypeStr])

  const batterySizeOpts = React.useMemo(
    (): c.Opts => c.convertToOptions(batterySize),
    [batterySize],
  )

  return [
    installers,
    installer ? batteryTypes : (c.emptyArr as typeof batteryTypes),
    batterySizeOpts,
  ] as const
}

export const initProvider = ({
  fireStorage,
  getUserEmail,
  getUserID,
}: c.InitStoreParams) => {
  const store = c.initStore({
    fireStorage,
    getUserEmail,
    getUserID,
  })

  if (c.IS_WEB) {
    // @ts-ignore
    window.store = store
  }

  return React.memo<{
    children: React.ReactElement
    loading?: React.ReactElement
  }>(({ children }) => <Provider store={store}>{children}</Provider>)
}

interface IChangeEvent {
  readonly target: {
    readonly value: string
  }
}

export const useInput = (initialState: string) => {
  const [currState, setState] = useState<string>(initialState)

  const onChange = useCallback((e: IChangeEvent): void => {
    setState(e.target.value)
  }, [])

  return [currState, setState, onChange] as const
}

export const useTextInput = (initialState: string) => {
  const [currState, setState] = useState<string>(initialState)

  const onChangeText = useCallback((text: string): void => {
    setState(text)
  }, [])

  return [currState, setState, onChangeText] as const
}

export function useCustomer(customerID: string) {
  const selectCustomer = React.useMemo(
    (): ReturnType<typeof c.makeSelectCustomer> => c.makeSelectCustomer(),
    [],
  )

  const selectCustomerArgs = React.useMemo(
    (): c.SelectCustomerParams => ({
      customerID,
    }),
    [customerID],
  )

  const currentCustomer = c.useSelector(
    (_): c.Customer => selectCustomer(_, selectCustomerArgs),
  )

  return [currentCustomer] as const
}

/**
 * Accepts an empty customerID for flexibility of use. If empty, it will not
 * read or write from/to the DB.
 */
export const useCustomerField = <K extends c.CustomerField>(
  customerID: string,
  field: K,
) => {
  type FieldVal = c.Customer[K]
  const [customer] = useCustomer(customerID)
  const remoteValue = customer[field]
  const [localValue, setLocalValue] = React.useState<FieldVal>(remoteValue)
  // 13332482513: see common/helpers/editEchoGuard.ts.
  const guard = React.useRef(c.createEditEchoGuard())
  const remoteRef = React.useRef(remoteValue)
  remoteRef.current = remoteValue

  React.useEffect((): void => {
    // The store only hears of a save through the batched database echo, so a
    // batch can carry an older keystroke than the input already shows. While
    // this field's own edits settle, their echoes never replace the input.
    if (!c.shouldTakeRemote(guard.current, remoteValue, Date.now())) return
    // api._updateCustomer trims every string it saves, so typing "Charles "
    // saves "Charles" and that value comes straight back. Copying it into the
    // input erased the space the user had just typed (Jaron, Oct 19 video,
    // 13332482513). What comes back equal to the input minus its trailing or
    // leading spaces is our own save: keep what the user typed.
    setLocalValue((prev) =>
      typeof prev === 'string' &&
      typeof remoteValue === 'string' &&
      prev !== remoteValue &&
      prev.trim() === remoteValue
        ? prev
        : remoteValue,
    )
  }, [remoteValue])

  type SetFieldAction = React.SetStateAction<FieldVal>
  type FieldDispatch = React.Dispatch<SetFieldAction>

  const writeValue = React.useCallback<FieldDispatch>(
    (newValueOrDispatch) => {
      if (typeof newValueOrDispatch === 'function') {
        setLocalValue((prev) => {
          const newVal = newValueOrDispatch(prev)
          c.noteLocalEdit(
            guard.current,
            newVal,
            remoteRef.current,
            Date.now(),
            true,
          )
          setImmediate(() => c.updateCustomer(customerID, { [field]: newVal }))
          return newVal
        })
      } else {
        const newVal = newValueOrDispatch
        c.noteLocalEdit(
          guard.current,
          newVal,
          remoteRef.current,
          Date.now(),
          true,
        )
        setLocalValue(newValueOrDispatch)
        setImmediate(() => c.updateCustomer(customerID, { [field]: newVal }))
      }
    },
    [customerID, field],
  )

  const toggleValue = React.useCallback(() => {
    // @ts-expect-error
    setLocalValue((curr) => {
      const newVal = !curr
      c.noteLocalEdit(
        guard.current,
        newVal,
        remoteRef.current,
        Date.now(),
        true,
      )
      c.updateCustomer(customerID, { [field]: newVal })
      return newVal
    })
  }, [customerID, field])

  return [localValue as c.Customer[K], writeValue, toggleValue] as const
}

export const useSessionVal = <Cast extends c.DBVal>(key: string) => {
  const selectSessionVal = React.useMemo(() => c.makeSelectSessionVal(), [])
  const selectSessionValArgs = React.useMemo(
    (): c.SelectSessionValParams => ({
      key,
    }),
    [key],
  )
  const currVal = c.useSelector(
    (_): c.DBVal => selectSessionVal(_, selectSessionValArgs),
  ) as Cast

  type WriteVal = Dispatch<SetStateAction<Cast>>
  const writeVal = React.useCallback<WriteVal>(
    (valueOrSetter) => {
      c.dispatch(
        c.setSessionVal({
          key,
          value:
            typeof valueOrSetter === 'function'
              ? valueOrSetter(currVal)
              : valueOrSetter,
        }),
      )
    },
    [currVal, key],
  )

  const toggleVal = React.useCallback(() => {
    c.dispatch(
      c.setSessionVal({
        key,
        value: !currVal,
      }),
    )
  }, [currVal, key])

  return [currVal, writeVal, toggleVal] as const
}

export const useCustomerFieldInput = (
  customerID: string,
  field: c.CustomerField,
) => {
  const [customerData] = useCustomer(customerID)
  const remote = customerData[field] as string

  const [localVal, setLocalVal] = React.useState('')
  // 13332482513: see common/helpers/editEchoGuard.ts. The save waits 100 ms
  // and the effect below used to copy the store into the input on ANY change
  // to the customer (a sort_key echo was enough), dropping the keys typed
  // since the last save.
  const guard = React.useRef(c.createEditEchoGuard())
  const remoteRef = React.useRef(remote)
  remoteRef.current = remote

  const { current: inputEvBatcher } = React.useRef(
    c.createEventBatcher<string>({
      debounceTime: 100,
    }),
  )
  React.useEffect(() => {
    inputEvBatcher.onEvents((updates): void => {
      if (updates.length > 0) {
        c.noteEditSent(guard.current, Date.now())
        c.updateCustomer(customerID, {
          [field]: updates[updates.length - 1],
        })
      }
    })
    return (): void => {
      inputEvBatcher.flush()
      inputEvBatcher.off()
    }
  }, [customerID, field, inputEvBatcher])

  const write = React.useCallback(
    (newVal: string) => {
      c.noteLocalEdit(
        guard.current,
        newVal,
        remoteRef.current,
        Date.now(),
        false,
      )
      setLocalVal(newVal)
      inputEvBatcher.eventReceived(newVal)
    },
    [inputEvBatcher],
  )

  const handle = React.useCallback(
    (e: { target: { value: string } }): void => {
      write(e.target.value)
    },
    [write],
  )

  React.useEffect(() => {
    if (!c.shouldTakeRemote(guard.current, remote, Date.now())) return
    setLocalVal(remote)
  }, [remote])

  return [localVal, write, handle] as const
}

/**
 * A text field saved `saveMs` after the last keystroke: the Other provider
 * fields on the app's Security screen and on the report. One copy for both,
 * under the shared echo rule (13332482513, editEchoGuard.ts). Each old copy
 * stopped ignoring the database the moment it saved, so the trimmed echo of
 * "Brinks " or of a new empty line, or a late batch, replaced the input.
 */
export const useCustomerDraftField = (
  customerID: string,
  field: c.CustomerField,
  saveMs = 600,
): readonly [string, (val: string) => void] => {
  const [customer] = useCustomer(customerID)
  const remote = (customer[field] as string) || ''
  const [draft, setDraft] = React.useState(remote)
  const guard = React.useRef(c.createEditEchoGuard())
  const remoteRef = React.useRef(remote)
  remoteRef.current = remote
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (c.shouldTakeRemote(guard.current, remote, Date.now())) setDraft(remote)
  }, [remote])

  const flush = React.useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    if (pending.current !== null) {
      c.noteEditSent(guard.current, Date.now())
      c.updateCustomer(customerID, { [field]: pending.current })
      pending.current = null
    }
  }, [customerID, field])

  React.useEffect(() => flush, [flush])

  const write = React.useCallback(
    (val: string) => {
      c.noteLocalEdit(guard.current, val, remoteRef.current, Date.now(), false)
      pending.current = val
      setDraft(val)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(flush, saveMs)
    },
    [flush, saveMs],
  )

  return [draft, write] as const
}

/**
 * The rows of the other provider's equipment (Jaron's mockup, Oct 18): a
 * product from a fixed list, a quantity, and a photo of that piece at the
 * customer's home. Saved 600 ms after the last change, like the text fields,
 * under the same echo rule (13332482513). Rows start from the old free-text
 * lines the first time one is edited, and those lines are never deleted (a
 * build without rows keeps showing them).
 */
export const useOtherProviderEquipmentRows = (customerID: string) => {
  const [customer] = useCustomer(customerID)
  const remote = c.equipmentRowsFor(
    customer.other_provider_equipment_rows,
    customer.other_provider_equipment,
  )
  const remoteKey = c.serializeEquipmentRows(remote)
  const [rows, setRows] = React.useState(remote)
  const rowsRef = React.useRef(rows)
  const guard = React.useRef(c.createEditEchoGuard())
  const remoteKeyRef = React.useRef(remoteKey)
  remoteKeyRef.current = remoteKey
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const dirty = React.useRef(false)

  React.useEffect(() => {
    if (!c.shouldTakeRemote(guard.current, remoteKey, Date.now())) return
    rowsRef.current = remote
    setRows(remote)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteKey])

  const flush = React.useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    if (dirty.current) {
      c.noteEditSent(guard.current, Date.now())
      c.updateCustomer(customerID, {
        other_provider_equipment_rows: c.serializeEquipmentRows(
          rowsRef.current,
        ),
      })
      dirty.current = false
    }
  }, [customerID])

  React.useEffect(() => flush, [flush])

  const write = React.useCallback(
    (next: c.OtherProviderEquipmentRow[], now?: boolean) => {
      rowsRef.current = next
      setRows(next)
      // Remembered the way the store reads it back (types trimmed on read).
      c.noteLocalEdit(
        guard.current,
        c.serializeEquipmentRows(
          c.equipmentRowsFor(c.serializeEquipmentRows(next), ''),
        ),
        remoteKeyRef.current,
        Date.now(),
        false,
      )
      dirty.current = true
      if (timer.current) clearTimeout(timer.current)
      if (now) flush()
      else timer.current = setTimeout(flush, 600)
    },
    [flush],
  )

  return { rows, rowsRef, write } as const
}

export const useEnv = <Cast extends c.DBVal>(key: string): Cast | undefined => {
  // In the future, env will be separate from settings
  const selectEnv = React.useMemo(() => c.makeSelectEnv(), [])
  const selectEnvArgs = React.useMemo(
    (): c.SelectEnvParams => ({
      key,
    }),
    [key],
  )
  const currVal = c.useSelector(
    (_): c.DBVal => selectEnv(_, selectEnvArgs),
  ) as Cast

  return currVal
}

export const ghostDiv: HTMLDivElement = (() => {
  if (c.IS_WEB) {
    // @ts-ignore TODO
    return document.createElement('div')
  }
  return null as unknown as HTMLDivElement
})()
export const ghostInput: HTMLInputElement = (() => {
  if (c.IS_WEB) {
    // @ts-ignore TODO
    return document.createElement('input')
  }
  return null as unknown as HTMLInputElement
})()
export const ghostP = (() => {
  if (c.IS_WEB) {
    // @ts-ignore TODO
    return document.createElement('p')
  }
  return null as unknown as HTMLParagraphElement
})()

//#region Rowable

export interface RowableUsed<T extends c.Rowable = c.Rowable> {
  addRow(): void
  changeVal(
    col: string,
    rowID: string,
    val: React.SetStateAction<number | string>,
  ): void
  cols: c.Cols
  deleteRow(rowID: string): void
  rows: readonly T[]
}

export const useRowable = <T extends c.Rowable = c.Rowable>(
  customerID: string,
  field: c.FieldRowable,
): RowableUsed<T> => {
  const [createdAt] = useCustomerField(customerID, 'createdAt')
  const [homeRep] = useCustomerField(customerID, 'homeRep')
  const [company] = useCustomerField(customerID, 'solarCompany')
  const state = c.getCompanyState(company) as
    | 'arizona'
    | 'california'
    | 'nevada'

  //#region global
  const windowColors: readonly string[] = c.parseEnvStringList(
    useEnv('windowColors'),
  )

  const secCamPrices = c.useSelector(c.selectEfficiencyPrice('security_cams'))
  const secCamItems = useEnv<c.Opts>('security_cams') || c.emptyOpts

  const secSysPrices = c.useSelector(c.selectEfficiencyPrice('security_system'))
  const secSysItems = useEnv<c.Opts>('security_system') || c.emptyOpts

  const secTVPrices = c.useSelector(c.selectEfficiencyPrice('security_tv'))
  const secTVItems = useEnv<c.Opts>('security_tv') || c.emptyOpts

  const secWifiCamPrices = c.useSelector(
    c.selectEfficiencyPrice('security_wifi_cameras'),
  )
  const secWifiCamItems = useEnv<c.Opts>('security_wifi_cameras') || c.emptyOpts

  const secHomeAutoPrices = c.useSelector(
    c.selectEfficiencyPrice('security_home_automation'),
  )
  const secHomeAutoItems =
    useEnv<c.Opts>('security_home_automation') || c.emptyOpts

  const ceilingFanTypes = useEnv<c.strings>('ceilingFanTypes')
  //#endregion

  const cols = React.useMemo((): c.Cols => {
    const _cols = c.rowableToCols[field]

    if (field === 'ceiling_fan_detail') {
      const theCols = _cols.slice()
      ;(theCols[0] as c.Writable<c.OptCol>).opts =
        ceilingFanTypes?.map((value) => ({
          label: value,
          value,
        })) || c.EMPTY_ARRAY

      return theCols
    }
    if (field === 'security_cams_detail') {
      const theCols = _cols.slice()

      ;(theCols[0] as c.Writable<c.OptCol>).opts = secCamItems

      return theCols
    }
    if (field === 'security_system_detail') {
      const theCols = _cols.slice()

      ;(theCols[0] as c.Writable<c.OptCol>).opts = secSysItems

      return theCols
    }
    if (field === 'security_wifi_cameras_detail') {
      const theCols = _cols.slice()

      ;(theCols[0] as c.Writable<c.OptCol>).opts = secWifiCamItems

      return theCols
    }
    if (field === 'security_home_automation_detail') {
      const theCols = _cols.slice()

      ;(theCols[0] as c.Writable<c.OptCol>).opts = secHomeAutoItems

      return theCols
    }
    if (field === 'security_tv_detail') {
      const theCols = _cols.slice()

      ;(theCols[0] as c.Writable<c.OptCol>).opts = secTVItems

      return theCols
    }
    if (field === 'new_windows_sqft_each_window') {
      const shouldShowGlobalColor = createdAt > c.Jun4_2024

      const theCols = _cols.slice()

      if (windowColors)
        (theCols[2] as c.OptCol) = {
          ...(theCols[2] as c.OptCol),
          opts: windowColors.map(
            (c): c.Opt => ({
              label: c,
              value: c,
            }),
          ),
        }

      const isGiovanniCustomer = homeRep.toLowerCase().includes('giovanni')
      // always an old customer
      if (isGiovanniCustomer) {
        theCols.push(c.windowTypeCol)
      }

      if (shouldShowGlobalColor) {
        theCols.splice(2, 1)
        theCols[0] = {
          ...theCols[0],
          widthPer: 30,
        } as c.Col
        theCols[1] = {
          ...theCols[1],
          widthPer: 30,
        } as c.Col
        theCols[2] = {
          ...theCols[2],
          widthPer: 30,
        } as c.Col
      }

      // Remove individual color selector for newer customers
      return theCols
    }

    return _cols
  }, [
    ceilingFanTypes,
    createdAt,
    field,
    homeRep,
    secCamItems,
    secHomeAutoItems,
    secSysItems,
    secTVItems,
    secWifiCamItems,
    windowColors,
  ])

  const [rowsStr, setRowsStr] = useCustomerField(customerID, field)

  const addRow = React.useCallback<RowableUsed['addRow']>((): void => {
    setRowsStr((currRowsStr) => {
      const newRows = JSON.parse(currRowsStr || '[]') as c.Rowable[]

      if (field === 'can_light_detail') {
        ;(newRows as c.CanLightGroup[]).push(c.newCanLightGroup())
      }
      if (field === 'ceiling_fan_detail') {
        ;(newRows as c.CeilingFan[]).push(c.newCeilingFan())
      }
      if (field === 'security_cams_detail') {
        ;(newRows as c.CamGroup[]).push(c.newCamGroup())
      }
      if (field === 'security_wifi_cameras_detail') {
        ;(newRows as c.CamGroup[]).push(c.newCamGroup())
      }
      if (field === 'security_home_automation_detail') {
        ;(newRows as c.CamGroup[]).push(c.newCamGroup())
      }
      if (field === 'security_system_detail') {
        ;(newRows as c.SecSysGroup[]).push(c.newSecSysGroup())
      }
      if (field === 'security_tv_detail') {
        ;(newRows as c.SecurityTV[]).push(c.newSecurityTV())
      }
      if (field === 'mini_split_tons') {
        ;(newRows as c.MiniSplit[]).push(c.newMiniSplit())
      }
      if (field === 'new_windows_sliding_glass_sqft') {
        ;(newRows as c.SlidingGlassDoorGroup[]).push(
          c.newSlidingGlassDoorGroup(),
        )
      }
      if (field === 'new_windows_sqft_each_window') {
        ;(newRows as c.WindowGroup[]).push(c.newWindowGroup())
      }
      if (field === 'outlet_detail') {
        ;(newRows as c.OutletGroup[]).push(c.newOutletGroup())
      }

      return JSON.stringify(newRows)
    })
  }, [field, setRowsStr])

  const changeVal = React.useCallback<RowableUsed['changeVal']>(
    (colKey, rowID, val): void => {
      if (field === 'security_cams_detail' && !secCamPrices) {
        console.debug('Security cam prices not ready, aborting')
        return
      }
      if (field === 'security_wifi_cameras_detail' && !secWifiCamPrices) {
        console.debug('Security wifi cam prices not ready, aborting')
        return
      }
      if (field === 'security_home_automation_detail' && !secHomeAutoPrices) {
        console.debug('Security home automation prices not ready, aborting')
        return
      }
      if (field === 'security_system_detail' && !secSysPrices) {
        console.debug('Security system prices not ready, aborting')
        return
      }
      if (field === 'security_tv_detail' && !secTVPrices) {
        console.debug('Security tv prices not ready, aborting')
        return
      }
      setRowsStr((currRowsStr) => {
        const newRows = JSON.parse(
          currRowsStr || '[]',
        ) as c.Writable<c.Rowable>[]

        const theRow = newRows.find((r) => r.id === rowID)

        if (!theRow) {
          // The row was deleted
          return currRowsStr
        }

        const colType = cols.find((_) => _.key === colKey)!.type

        const newVal = (() => {
          if (typeof val === 'function') {
            return val(theRow[colKey as keyof typeof theRow])
          } else {
            return val
          }
        })()
        const newValSanitized =
          colType === 'input'
            ? (newVal as string).replace('-', '').replace('+', '')
            : // Why was this needed?
              // .replace(',', '')
              newVal

        // A row's notes belong to whichever product currently occupies it —
        // not to the row itself. Switching a row to a DIFFERENT product must
        // always reset notes to the new product's auto-note (or clear it),
        // discarding whatever was there before, auto-generated or hand-typed
        // (Samuel, 2026-07-28: "las notas manuales deben quedar anidadas al
        // producto"). Guarded on an actual change so re-selecting the same
        // product already on the row never clobbers an edited note.
        const oldType =
          colKey === 'type' ? (theRow as { type?: string }).type : undefined

        theRow[colKey as keyof typeof theRow] = newValSanitized as any

        // Price lookup for the five security detail sections below: `?.value
        // ?? 0`, NOT `find(...)!.value`. If the row's product is no longer in
        // the price list (renamed / removed from the catalog — see V39, the
        // Misc/env vs EfficiencyPrices divergence), `find` returns undefined
        // and the non-null assertion threw "Cannot read property 'value' of
        // undefined", killing the whole section via its error boundary
        // (Security.tsx). B146 — the calculator was hardened for this three
        // times (12800305583 / 12807193683 / 12807218729) but the EDITOR was
        // never touched, so the crash survived every "fix" because it only
        // fires on edit, not on calc. An unknown product now prices at 0 and
        // the row stays editable, exactly as the calculator already does
        // (helpers.ts calcSecurity* -> `?.value ?? 0`), so a rep can open the
        // customer and correct the row.
        if (field === 'security_cams_detail' && (theRow as c.CamGroup).type) {
          const prices = c.priceRowsFor(secCamPrices!, state)
          const price =
            prices.find((p) => p.label === (theRow as c.CamGroup).type)
              ?.value ?? 0

          ;(theRow as c.Writable<c.CamGroup>).price = price
          if (colKey === 'type' && (theRow as c.CamGroup).type !== oldType) {
            ;(theRow as c.Writable<c.CamGroup>).notes =
              c.securityAutoNotes[(theRow as c.CamGroup).type] || ''
          }
        }

        if (
          field === 'security_wifi_cameras_detail' &&
          (theRow as c.CamGroup).type
        ) {
          const prices = c.priceRowsFor(secWifiCamPrices!, state)
          const price =
            prices.find((p) => p.label === (theRow as c.CamGroup).type)
              ?.value ?? 0

          ;(theRow as c.Writable<c.CamGroup>).price = price
          if (colKey === 'type' && (theRow as c.CamGroup).type !== oldType) {
            ;(theRow as c.Writable<c.CamGroup>).notes =
              c.securityAutoNotes[(theRow as c.CamGroup).type] || ''
          }
        }

        if (
          field === 'security_home_automation_detail' &&
          (theRow as c.CamGroup).type
        ) {
          const prices = c.priceRowsFor(secHomeAutoPrices!, state)
          const price =
            prices.find((p) => p.label === (theRow as c.CamGroup).type)
              ?.value ?? 0

          ;(theRow as c.Writable<c.CamGroup>).price = price
          if (colKey === 'type' && (theRow as c.CamGroup).type !== oldType) {
            ;(theRow as c.Writable<c.CamGroup>).notes =
              c.securityAutoNotes[(theRow as c.CamGroup).type] || ''
          }
        }

        if (
          field === 'security_system_detail' &&
          !!(theRow as c.SecSysGroup).type
        ) {
          const prices = c.priceRowsFor(secSysPrices!, state)

          const price =
            prices.find((p) => p.label === (theRow as c.SecSysGroup).type)
              ?.value ?? 0

          ;(theRow as c.Writable<c.SecSysGroup>).price = price
          if (colKey === 'type' && (theRow as c.SecSysGroup).type !== oldType) {
            ;(theRow as c.Writable<c.SecSysGroup>).notes =
              c.securityAutoNotes[(theRow as c.SecSysGroup).type] || ''
          }
        }

        if (colKey === 'qty') {
          const multiInputKey = cols.find((c) => c.type === 'multi-input')?.key
          if (multiInputKey) {
            const currVals = JSON.parse(
              theRow[multiInputKey as keyof typeof theRow],
            )

            const qty = c.parseInt((theRow as any).qty)
            if (currVals.length !== qty) {
              const newArr = c.resizeArray(currVals, qty, '')
              theRow[multiInputKey as keyof typeof theRow] =
                JSON.stringify(newArr)
            }
          }
        }

        if (field === 'security_tv_detail' && !!(theRow as c.SecurityTV).type) {
          const prices = c.priceRowsFor(secTVPrices!, state)

          const price =
            prices.find((p) =>
              p.label.startsWith((theRow as c.SecurityTV).type),
            )?.value ?? 0

          ;(theRow as c.Writable<c.SecurityTV>).price = price
        }

        if (colKey === 'qty') {
          const multiInputKey = cols.find((c) => c.type === 'multi-input')?.key
          if (multiInputKey) {
            const currVals = JSON.parse(
              theRow[multiInputKey as keyof typeof theRow],
            )

            const qty = c.parseInt((theRow as any).qty)
            if (currVals.length !== qty) {
              const newArr = c.resizeArray(currVals, qty, '')
              theRow[multiInputKey as keyof typeof theRow] =
                JSON.stringify(newArr)
            }
          }
        }

        return JSON.stringify(newRows)
      })
    },
    [
      cols,
      field,
      secCamPrices,
      secHomeAutoPrices,
      secSysPrices,
      secTVPrices,
      secWifiCamPrices,
      setRowsStr,
      state,
    ],
  )

  const deleteRow = React.useCallback<RowableUsed['deleteRow']>(
    (rowID) => {
      setRowsStr((currRowsStr) => {
        const newRows = JSON.parse(currRowsStr) as c.Rowable[]

        const rowIdx = newRows.findIndex((r) => r.id === rowID)

        if (rowIdx === 0 && newRows.length === 1) {
          if (field === 'mini_split_tons') {
            return JSON.stringify([c.newMiniSplit()])
          }
          if (field === 'new_windows_sliding_glass_sqft') {
            return JSON.stringify([c.newSlidingGlassDoorGroup()])
          }
          if (field === 'new_windows_sqft_each_window') {
            return JSON.stringify([c.newWindowGroup()])
          }
        }

        newRows.splice(rowIdx, 1)
        return JSON.stringify(newRows)
      })
    },
    [field, setRowsStr],
  )

  // B120: was re-parsing the rows JSON on every render and handing back a new
  // array identity each time, so every consumer memoized on `rows` recomputed
  // too. This hook drives the product-row pickers on the Security tab, which
  // renders eight of them.
  const rows = React.useMemo(
    (): T[] => JSON.parse(rowsStr || '[]') as T[],
    [rowsStr],
  )

  if (!c.rowableFields.includes(field)) {
    throw new Error(`Wrong field: ${field}`)
  }

  return {
    addRow,
    changeVal,
    cols,
    deleteRow,
    rows,
  }
}
//#endregion

export const useWindowColors = (): c.Opts => {
  const windowColors = c.parseEnvStringList(useEnv('windowColors'))
  const windowColorOpts = windowColors.map(
    (c): c.Opt => ({
      label: c,
      value: c,
    }),
  )
  return windowColorOpts
}

export const useInsulation = (customerID: string): c.Opts => {
  const [createdAt] = useCustomerField(customerID, 'createdAt')
  const [solarCompany] = useCustomerField(customerID, 'solarCompany')
  const [currInsulationType] = useCustomerField(
    customerID,
    'attic_insulation_type',
  )

  const insulationTypes = React.useMemo((): c.Opts => {
    if (typeof createdAt !== 'number') {
      console.warn(
        `useInsulation() -> ${customerID} customer's createdAt not a number: ${typeof createdAt}`,
      )
      return c.emptyArr as c.Opts
    }
    if (!solarCompany) {
      return c.emptyArr as c.Opts
    }

    // White labels won't have such old customers
    const isOldCustomer = moment(createdAt).isBefore(
      c.NEW_INSULATION_TYPES_CUTOFF,
    )
    const insulationTypesToUse = isOldCustomer
      ? c.insulationTypesOld[solarCompany]
      : c.companyToInsulationTypes[solarCompany]

    if (!insulationTypesToUse) {
      return [
        {
          label: currInsulationType,
          value: currInsulationType,
        },
      ]
    }

    return insulationTypesToUse.map((value) => ({
      label: value,
      value,
    }))
  }, [createdAt, currInsulationType, customerID, solarCompany])

  return insulationTypes
}

export const useTotalCost = (
  type: 'company' | 'customer',
  customerID: string,
  deductDisabled?: boolean,
): number => {
  const twoStoryFee = useEnv<number>('twoStoryFee') || 0

  // c.debug('useTotalCost()')
  const customer = c.useSelector((s) => c.selectCustomer(s, customerID))
  if (!customer) {
    // c.debug('useTotalCost() -> returning 0 as no customer found')
    return 0
  }

  return c.calcTotalCost({
    customer,
    deductDisabled,
    // Must be mirrored in usePayment()
    twoStoryFee,
    type,
  })
}

export const MUIBreakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
  laptop: 1090,
} as const

/**
 * Widest the content is allowed to get before it stops growing and centres
 * itself instead. Sized from the layout the screens were actually designed
 * against - a large phone - so on a phone the constraint is never reached and
 * nothing about the current rendering changes. It only has an effect where the
 * viewport is wider than any phone, which is the iPad case.
 */
export const CONTENT_MAX_WIDTH = 700

/**
 * The app was built phone-first with no notion of a tablet anywhere, so on an
 * iPad every screen stretches its phone layout across the full width - rows
 * running the whole diagonal, inputs stranded far from their labels.
 *
 * Reports the width alongside the verdict rather than a bare boolean, because
 * an iPad resizes while the app is running: rotation, Split View and Slide Over
 * all change the window without remounting anything. useWindowDimensions
 * already tracks that, so a screen reading this re-renders at the right size on
 * its own - which Dimensions.get(), used everywhere else in this codebase,
 * does not do.
 */
export const useIsWide = (): { isWide: boolean; width: number } => {
  const { width } = rn.useWindowDimensions()

  return React.useMemo(
    () => ({ isWide: width > CONTENT_MAX_WIDTH, width }),
    [width],
  )
}

//#region theming
export const useTheme = (colorScheme?: 'dark' | 'light') => {
  const _colorSchemeRN = rn.useColorScheme()
  const sysColorScheme =
    _colorSchemeRN === 'unspecified' ? 'light' : _colorSchemeRN

  return c.themeTuple[colorScheme || sysColorScheme || 'light']
}
//#endregion theming
//#region address
// const placesBaseURL = 'https://maps.googleapis.com/maps/api/place'

type Geometry = c.r<{ location: { lat: number; lng: number } }>

export type Prediction = c.r<{
  description: string
  /* Not actually contained in the autocomplete endpoint, but in the details one */
  geometry: Geometry
  place_id: string
}>

// let webAutocomplete: google.maps.places.AutocompleteService =
//   null as unknown as google.maps.places.AutocompleteService

export const setGoogService = (
  //@ts-ignore
  s: google.maps.places.AutocompleteService,
): void => {
  // @ts-ignore
  if (Math.random() === s) {
  } // avoid unused var
  // webAutocomplete = s
}

// const getPredictionsForAddress = async (q: string): Promise<c.Opts> => {
//   return Promise.resolve([])
//   // if (c.IS_WEB)
//   //   return new Promise((res, rej) => {
//   //     webAutocomplete.getQueryPredictions(
//   //       {
//   //         input: q,
//   //       },
//   //       (data, status) => {
//   //         if (status === 'OK') {
//   //           res(
//   //             data?.map((p) => ({
//   //               label: p.description,
//   //               value: p.place_id!,
//   //             })) || [],
//   //           )
//   //         } else {
//   //           rej(new Error(status))
//   //         }
//   //       },
//   //     )
//   //   })

//   // const placesURL = `${placesBaseURL as string}/autocomplete/json?key=${process
//   //   .env['REACT_APP_G_API_KEY']!}&language=en&components=country:us&input=`
//   // type PlacesRes = c.r<{
//   //   predictions: Prediction[]
//   // }>
//   // const res = await axios.request({
//   //   method: 'get',
//   //   url: `${placesURL}${q}`,
//   // })
//   // if (res.status === 200)
//   //   return (res.data as PlacesRes).predictions.map(
//   //     (p): c.Opt => ({ label: p.description, value: p.place_id }),
//   //   )
//   // else throw new Error(`${getPredictionsForAddress.name} -> res.status != 200`)
// }

// const getPredictionDetails = async (
//   placeID: string,
// ): Promise<Prediction | null> => {
//   return Promise.resolve(null)
//   if (c.IS_WEB) return new Promise((res, rej) => {
//     res()
//   })

//   const placesDetailsURL = `${placesBaseURL}/details/json?key=${process.env[
//     'REACT_APP_G_API_KEY'
//   ]!}&place_id=`
//   type PlaceRes = c.r<{ result: Prediction }>
//   const res = await axios.request({
//     method: 'get',
//     url: placesDetailsURL + placeID,
//   })
//   if (res.status === 200) return (res.data as PlaceRes).result
//   else throw new Error(`${getPredictionDetails.name} -> res.status != 200`)
// }

export const usePredictions = (
  customerID: string,
  q: string,
  gApiKey: string,
) => {
  if (Math.random() > 1) console.log(customerID, q, gApiKey) // avoid unused var

  return [[] as c.Opts, c.emptyFn] as const
  // const [predictions, setPredictions] = React.useState<c.Opts>([])

  // React.useEffect((): void => {
  //   if (q === '') return void setPredictions(c.EMPTY_ARRAY)
  //   const thisQ = q

  //   thisQ &&
  //     getPredictionsForAddress(thisQ).then((p) => {
  //       if (q === thisQ) setPredictions(p)
  //     })
  // }, [gApiKey, q])

  // const handlePredictionSelect = React.useCallback(
  //   async (placeID: string): Promise<void> => {
  //     const place = predictions.find((p) => p.value === placeID)!
  //     c.updateCustomer(customerID, {
  //       addressLat: 0,
  //       addressLng: 0,
  //       customerAddress: place.label,
  //     })
  //   },
  //   [customerID, predictions],
  // )

  // const [currAddress] = useCustomerField(customerID, 'customerAddress')
  // const [currLat] = useCustomerField(customerID, 'addressLat')
  // const [currLng] = useCustomerField(customerID, 'addressLng')

  // /* Fetch the coordinates for an existing address if the address is actually
  //  recognized by Google */
  // React.useEffect(() => {
  //   ;(async () => {
  //     // const [prediction = { label: '', value: '' }] = predictions
  //     // if (prediction.label === currAddress && currLat === 0 && currLng === 0) {
  //     //   c.debug(`Fetching coordinates for address ${currAddress}`, {
  //     //     predictions,
  //     //     currLat,
  //     //     currLng,
  //     //   })
  //     //   const fullPlace = await getPredictionDetails(prediction.value)
  //     //   if (fullPlace?.geometry) {
  //     //     c.updateCustomer(customerID, {
  //     //       addressLat: fullPlace.geometry.location.lat,
  //     //       addressLng: fullPlace.geometry.location.lng,
  //     //     })
  //     //   } else {
  //     //     c.debug(`No geometry for ${currAddress}`)
  //     //   }
  //     // }
  //   })()
  // }, [currAddress, currLat, currLng, customerID, predictions])

  // return [predictions, handlePredictionSelect] as const
}

/**
 * Opens a place in Google Maps.
 *
 * Takes either coordinates or a plain address, because a customer who was never
 * geocoded still has an address worth opening — and until 2026-08-26 the pin
 * beside the address simply did nothing for those records, with no message. That
 * is what Jaron reported as "should take you to Google Maps": the feature was
 * there, it just stayed silent whenever `addressLat`/`addressLng` were 0.
 *
 * The iOS branch used to be `maps://`, which is Apple Maps — so even when it did
 * fire it opened the wrong app. The universal Google URL is handled by the
 * Google Maps app when it is installed and falls back to the browser when it is
 * not, on both platforms.
 */
export const openMapsAt = async (query: string): Promise<void> => {
  const trimmed = query.trim()
  if (!trimmed) return

  await rn.Linking.openURL(
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      trimmed,
    )}`,
  ).catch((e): void => {
    rn.Alert.alert('Error', c.processErr(e))
  })
}
//#endregion address

export const empty = <rn.View />

export const isMobile = rn.Platform.OS === 'android' || rn.Platform.OS === 'ios'
export const isWeb = rn.Platform.OS === 'web'

export const isSafari = () => {
  if (isMobile) return false
  // @ts-ignore
  if (typeof navigator !== 'object' || navigator === null) return false
  // @ts-ignore
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

export const RowWrapper = isMobile ? rn.View : React.Fragment
// export const RowWrapper = React.Fragment;

/**
 * Returns a list of tags for the efficiencies turned on.
 */
export const useServicesRendered = (customerID: string): string => {
  const [customer] = useCustomer(customerID)

  const servicesRendered = React.useMemo(
    (): string[] =>
      c.nonCustomEfficiencyKeys
        .filter((k) => customer[k] === 'yes')
        .map((k) => c.getFieldLabel(k)),
    [customer],
  )

  const customFieldsUsed = c.customFields.filter((f) => customer[f] === 'yes')

  if (customFieldsUsed.length) {
    const customFieldsNamed = customFieldsUsed
      .map((f) => customer[(f + '_name') as c.CustomerField])
      .filter(c.identity) // reject empty names
      .join('/')

    return servicesRendered.concat(customFieldsNamed).join('/')
  }

  return servicesRendered.join('/') || '[No Services Rendered]'
}

export const useCRMUrl = (customerID: string): null | string => {
  const [crmID] = useCustomerField(customerID, 'crm_id')
  const [solarCompany] = useCustomerField(customerID, 'solarCompany')
  const companyToLocationID = useEnv('companyToCRMLocationID')

  if (!crmID) return null
  if (!c.isObj(companyToLocationID)) return null

  const locationID = companyToLocationID[solarCompany]

  if (!locationID) return null

  return `https://app.magnetixagency.com/v2/location/${locationID}/contacts/detail/${crmID}`
}

/**
 * If a product was enabled in the past but is now no longer available, it will
 * be returned in the list of available products.
 */
export const useProducts = (
  customerID: string,
  solarCompanyOverride?: string,
) => {
  const productToLabel = useEnv<c.Dict<string>>('productToLabel') || c.emptyObj

  const stateToProductsRaw =
    useEnv<c.Dict<string>>('stateToProducts') || c.emptyObj
  const stateToProducts = c.mapValues(
    stateToProductsRaw,
    // @ts-ignore
    (_) => JSON.parse(_) as c.strings,
  )
  // useCustomerField() update bug
  const [{ products, solarCompany }] = useCustomer(customerID)
  const currState = c.getCompanyState(solarCompanyOverride || solarCompany)
  const [, writeProducts] = useCustomerField(customerID, 'products')

  // Jaron (Slack #development-dpt, 2026-07-21): Alarm (Security), Home
  // Improvements (HIL) and Solar must be an option in EVERY state. Force this
  // base set on top of whatever the state defines in Firebase `stateToProducts`,
  // without editing that node. Only when a state is actually selected — with no
  // state we still show nothing, preserving the Add-Customer empty-until-state
  // behavior (T46).
  const BASE_PRODUCTS_ALL_STATES = ['Security', 'Solar', 'HIL']
  const availProductsForThisState = currState
    ? [
        ...(stateToProducts[currState] || c.EMPTY_ARRAY),
        ...BASE_PRODUCTS_ALL_STATES,
      ].filter(c.uniq)
    : stateToProducts[currState] || c.EMPTY_ARRAY

  const currProducts: c.strings =
    products === '' ? c.EMPTY_ARRAY : products.split(' / ')

  const toggleHIL = React.useCallback((): void => {
    if (currProducts.includes('HIL')) {
      writeProducts(currProducts.filter((p) => p !== 'HIL').join(' / '))
    } else {
      writeProducts(currProducts.concat('HIL').sort().join(' / '))
    }
  }, [currProducts, writeProducts])
  const toggleRC = React.useCallback((): void => {
    if (currProducts.includes('RC')) {
      writeProducts(currProducts.filter((p) => p !== 'RC').join(' / '))
    } else {
      writeProducts(currProducts.concat('RC').sort().join(' / '))
    }
  }, [currProducts, writeProducts])
  const toggleSecurity = React.useCallback((): void => {
    if (currProducts.includes('Security')) {
      writeProducts(currProducts.filter((p) => p !== 'Security').join(' / '))
    } else {
      writeProducts(currProducts.concat('Security').sort().join(' / '))
    }
  }, [currProducts, writeProducts])
  const toggleSolar = React.useCallback((): void => {
    if (currProducts.includes('Solar')) {
      writeProducts(currProducts.filter((p) => p !== 'Solar').join(' / '))
    } else {
      writeProducts(currProducts.concat('Solar').sort().join(' / '))
    }
  }, [currProducts, writeProducts])

  const availProductsFinal = React.useMemo((): c.Opts => {
    if (customerID) {
      return [...availProductsForThisState, ...currProducts]
        .sort()
        .reverse()
        .filter(c.uniq)
        .map((value) => ({
          label: productToLabel[value] || value,
          value,
        }))
    }

    // Adding a new customer: no state selected yet means no real options to
    // offer — show the real, state-specific list only once one is picked,
    // instead of every product regardless of availability.
    if (!solarCompanyOverride) return c.EMPTY_ARRAY

    return availProductsForThisState.map((value) => ({
      label: productToLabel[value] || value,
      value,
    }))
  }, [
    availProductsForThisState,
    currProducts,
    customerID,
    productToLabel,
    solarCompanyOverride,
  ])

  return {
    availProducts: availProductsFinal,
    currProducts,
    toggleHIL,
    toggleRC,
    toggleSecurity,
    toggleSolar,
  }
}

export type Focusable = c.r<{
  blur(): void
  focus(): void
}>

const unselectedContractCompany: c.ContractCompany = Object.freeze({
  address1: '[[Select a company for the contract]]',
  address2: '[[Select a company for the contract]]',
  logo: '',
  longName: '[[Select a company for the contract]]',
  phoneNumber: '[[Select a company for the contract]]',
  roofingLicense: '[[Select a company for the contract]]',
  shortName: '',
  states: '',
})
const jan282025 = c.normalizeTimestampToMs(1738072142000)

/**
 * Synthetic shortNames for the 2 "Other" contract company options — these do
 * NOT come from the shared `contractCompanies` Firebase node (that node is
 * for companies preconfigured once and reused across every customer). Other
 * is for one-off companies typed in per-customer — see V44/other_contract_*
 * fields in schema.ts.
 */
export const OTHER_CASH_SHORT_NAME = 'OTHER_CASH'
export const OTHER_FIN_SHORT_NAME = 'OTHER_FIN'

export const useContractCompany = (customerID: string) => {
  const contractCompaniesAllStates =
    useEnv<c.ContractCompanies>('contractCompanies') || c.emptyObj

  const [
    {
      contractCompany,
      createdAt,
      solarCompany: oldSolarCompanyVal,
      other_contract_company_name,
      other_contract_company_address,
      other_contract_company_phone,
      other_contract_company_license,
      other_contract_company_logo,
    },
  ] = useCustomer(customerID)

  const state = c.getCompanyState(oldSolarCompanyVal)
  const contractCompaniesCurrState = React.useMemo(
    (): c.ContractCompanies => ({
      ...c.pickBy(contractCompaniesAllStates, ({ states }) =>
        states.includes(state || ''),
      ),
      [OTHER_CASH_SHORT_NAME]: {
        address1: other_contract_company_address || '',
        address2: '',
        logo: other_contract_company_logo || '',
        longName: 'Other (Cash)',
        phoneNumber: other_contract_company_phone || '',
        roofingLicense: other_contract_company_license || '',
        shortName: OTHER_CASH_SHORT_NAME,
        states: '',
      },
      [OTHER_FIN_SHORT_NAME]: {
        address1: other_contract_company_address || '',
        address2: '',
        logo: other_contract_company_logo || '',
        longName: 'Other (Financing)',
        phoneNumber: other_contract_company_phone || '',
        roofingLicense: other_contract_company_license || '',
        shortName: OTHER_FIN_SHORT_NAME,
        states: '',
      },
    }),
    [
      contractCompaniesAllStates,
      state,
      other_contract_company_address,
      other_contract_company_license,
      other_contract_company_logo,
      other_contract_company_phone,
    ],
  )

  const contractCompanyFull: c.ContractCompany = (() => {
    if (
      contractCompany === OTHER_CASH_SHORT_NAME ||
      contractCompany === OTHER_FIN_SHORT_NAME
    ) {
      // Unlike the fixed "Other (Cash)"/"Other (Financing)" labels used in
      // the dropdown menu (contractCompaniesCurrState below), the rendered
      // contract itself must show the rep-typed company name — falling back
      // to the generic label only until they've typed one in.
      return {
        address1: other_contract_company_address || '',
        address2: '',
        logo: other_contract_company_logo || '',
        longName:
          other_contract_company_name ||
          (contractCompany === OTHER_CASH_SHORT_NAME
            ? 'Other (Cash)'
            : 'Other (Financing)'),
        phoneNumber: other_contract_company_phone || '',
        roofingLicense: other_contract_company_license || '',
        shortName: contractCompany,
        states: '',
      }
    }
    return (
      contractCompaniesAllStates[contractCompany] || unselectedContractCompany
    )
  })()

  const handleContractCompany = React.useCallback(
    (e: { target: { value: string } }): void => {
      c.updateCustomer(customerID, {
        contractCompany: e.target.value,
      })
    },
    [customerID],
  )

  // Independent of the contract company above: only the report cover logo.
  const handleCoverCompany = React.useCallback(
    (e: { target: { value: string } }): void => {
      c.updateCustomer(customerID, {
        coverCompany: e.target.value,
      })
    },
    [customerID],
  )

  const shouldOfferCompanySelector = createdAt > jan282025

  const contractCompaniesCurrStateArr = React.useMemo(
    () => c.values(contractCompaniesCurrState),
    [contractCompaniesCurrState],
  )

  // Real (Firebase-sourced) companies only — excludes the 2 synthetic "Other"
  // entries, which are always present and would otherwise break the
  // "auto-select if there's only one real company" behavior below.
  const realContractCompaniesArr = React.useMemo(
    () =>
      contractCompaniesCurrStateArr.filter(
        ({ shortName }) =>
          shortName !== OTHER_CASH_SHORT_NAME &&
          shortName !== OTHER_FIN_SHORT_NAME,
      ),
    [contractCompaniesCurrStateArr],
  )

  React.useEffect(() => {
    // If there's just one real company, auto-selected it
    if (shouldOfferCompanySelector && realContractCompaniesArr.length === 1) {
      const [theCompany] = realContractCompaniesArr
      handleContractCompany({ target: { value: theCompany!.shortName } })
    }
  }, [
    realContractCompaniesArr,
    handleContractCompany,
    shouldOfferCompanySelector,
  ])

  return {
    contractCompaniesAllStates,
    contractCompaniesCurrState,
    contractCompaniesCurrStateArr,
    contractCompanyFull,
    contractCompanyShortName: contractCompany,
    handleContractCompany,
    handleCoverCompany,
    shouldOfferCompanySelector,
  } as const
}

const feb10_2025 = 1739211101441
export const useSolarSurveyMedia = (customerID: string) => {
  const [createdAt] = useCustomerField(customerID, 'createdAt')
  const solarSurveyMediaKinds = useEnv<c.MediaKinds>('solarSurveyMediaKinds')

  if (createdAt > feb10_2025) {
    if (solarSurveyMediaKinds) {
      return [solarSurveyMediaKinds, c.mediaToLabelSolarSurveyNew] as const
    }
    return [c.solarSurveyMediaKindsNew, c.mediaToLabelSolarSurveyNew] as const
  }
  return [c.solarSurveyMediaKindsOld, c.mediaToLabelSolarSurveyOld] as const
}

export type Bullets = c.r<
  Array<{
    /**
     * Must be from the material community icon set inside
     * react-native-vector-icons
     */
    icon: string
    text: string
  }>
>

type Term = c.r<{
  /**
   * Dealer fee, in floating number format, 0.1 is 10%, 0.01 is 1%, and so on.
   */
  cost: number
  desc: string
  interest: number
  loanCode?: string
  maxAmt?: number
  /**
   * Minimum amount in dollars required for the loan.
   */
  minAmt?: number
  years: number
}>

export type LoanCompanies = c.r<{
  [K: string]: {
    states: string
    contractorID?: string
    programPhoneNumber?: string
    offers: {
      [x: string]: {
        bullets: Bullets
        choicesInMonths?: [firstBullet: string, secondBullet: string]
        desc: string
        /**
         * Must be from the material community icon set inside
         * react-native-vector-icons
         */
        icon: string
        plans?: {
          [x: string]: {
            /**
             * Not the actual length itself necessarily, but whatever applies
             * for any special promotions, as specified inside in the offer via
             * the optional choicesInMonths.
             */
            months?: number
            terms: {
              [x: string]: Term
            }
          }
        }
        shortDesc: string
        terms?: {
          [x: string]: Term
        }
      }
    }
  }
}>

/**
 * B120: this used to re-serialize the ENTIRE finance-company config (every
 * company with all of its offers, plans and terms) and run two full-string
 * replaceAll passes over it on EVERY render, with no memoization at all. That
 * is expensive on its own, but the worse part was the return value: a brand new
 * object identity every render, which silently defeated every downstream
 * useMemo keyed on it (usePayment's availLoanCompaniesNames /
 * availLoanCompaniesOpts recomputed every render as a result).
 *
 * It compounds on the Security tab: CheckGroup calls usePayment(), and
 * Security.tsx renders eight CheckGroups - so a single toggle paid for all of
 * the above eight times over. That is the reported lag when flipping Yes/No or
 * adding a product row (Monday 12707509093).
 */
export const useLoanCompanies = (customerID: string): LoanCompanies => {
  const loanCompaniesUnparsed = useEnv('loanCompanies')
  const [state] = useCustomerField(customerID, 'solarCompany')

  // Firebase doesn't support dots or slashes in object keys
  const loanCompanies = React.useMemo(
    (): LoanCompanies =>
      JSON.parse(
        JSON.stringify(
          loanCompaniesUnparsed || {
            'Loading...': {
              offers: {},
              states: '',
            },
          },
        )
          .replaceAll('(((dot)))', '.')
          .replaceAll('(((slash)))', '/'),
      ) as LoanCompanies,
    [loanCompaniesUnparsed],
  )

  return React.useMemo(
    (): LoanCompanies =>
      c.pickBy(loanCompanies, (lc) => lc.states.includes(state)),
    [loanCompanies, state],
  )
}

export const PAYMENT_METHOD_OPTS: c.Opts<c.PaymentMethod> = [
  {
    label: 'Financing',
    value: 'financing',
  },
  {
    label: 'Cash',
    value: 'cash',
  },
] as const

export const usePayment = (customerID: string) => {
  /**
   * Nirvana only
   */
  const twoStoryFee = useEnv<number>('twoStoryFee') || 0

  const CASH_DEALER_FEE = useEnv<number>('cashDealerFee') || 0
  const CASH_TERM = React.useMemo(
    (): Term => ({
      cost: CASH_DEALER_FEE,
      desc: 'Cash',
      interest: 0,
      years: 0,
    }),
    [CASH_DEALER_FEE],
  )

  const [paymentVal, paymentSet] = useCustomerField(
    customerID,
    'security_loan_applied',
  )
  const paymentData = React.useMemo((): c.PayMethodApplied | null => {
    if (typeof paymentVal === 'string' && paymentVal) {
      return JSON.parse(paymentVal) as c.PayMethodApplied
    }
    return null
  }, [paymentVal])
  const paymentDataRef = React.useRef(paymentData)
  paymentDataRef.current = paymentData
  const paymentHandle = React.useCallback(
    (partial: Partial<c.PayMethodApplied>): void => {
      paymentSet(
        JSON.stringify({ ...(paymentDataRef.current || {}), ...partial }),
      )
    },
    [paymentSet],
  )

  const [paymentIsApplied, paymentIsAppliedSet] = useCustomerField(
    customerID,
    'payment_is_applied',
  )

  const paymentMethodVal: c.PaymentMethod =
    (paymentData?.paymentMethod as c.PaymentMethod) ?? 'financing'
  const paymentMethodSet = React.useCallback(
    (val: c.PaymentMethod): void => {
      paymentHandle({ paymentMethod: val })
    },
    [paymentHandle],
  )

  const availLoanCompanies = useLoanCompanies(customerID!)
  const availLoanCompaniesNames = React.useMemo(
    () => Object.keys(availLoanCompanies),
    [availLoanCompanies],
  )
  const availLoanCompaniesOpts = React.useMemo(
    (): c.Opts =>
      Object.keys(availLoanCompanies).map((value) => ({ label: value, value })),
    [availLoanCompanies],
  )
  const loanCompanyName = paymentData?.loanCompanyName ?? ''
  const loanCompany = availLoanCompanies[loanCompanyName]
  const loanCompanyNameHandle = React.useCallback(
    (eOrVal: c.InputEvent | string): void => {
      const newVal = typeof eOrVal === 'string' ? eOrVal : eOrVal.target.value
      paymentHandle({
        loanCompanyName: newVal,
        offerName: '',
        planName: '',
        termInterest: 0,
        termName: '',
        termYears: 0,
      })
    },
    [paymentHandle],
  )

  const availOffers = React.useMemo(
    // Reverse sort makes it so "Standard" is on top, at least based on current
    // offers
    () =>
      Object.entries(loanCompany?.offers || c.emptyObj).sort(([a], [b]) =>
        b.localeCompare(a),
      ),
    [loanCompany?.offers],
  )
  const availOffersOpts = React.useMemo(
    (): c.Opts =>
      availOffers.map(([value]) => ({
        label: value,
        value,
      })),
    [availOffers],
  )
  const offerName = paymentData?.offerName ?? ''
  const offer = loanCompany?.offers[offerName]
  const offerNameHandle = React.useCallback(
    (eOrVal: c.InputEvent | string): void => {
      const newVal = typeof eOrVal === 'string' ? eOrVal : eOrVal.target.value
      paymentHandle({
        offerName: newVal,
        planName: '',
        termInterest: 0,
        termName: '',
        termYears: 0,
      })
    },
    [paymentHandle],
  )

  const availPlans = React.useMemo(
    () =>
      c
        .entries(offer?.plans || c.emptyObj)
        .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true })),
    [offer],
  )
  const availPlansOpts = React.useMemo(
    (): c.Opts =>
      availPlans.map(([value]) => ({
        label: value,
        value,
      })),
    [availPlans],
  )
  const planName = paymentData?.planName ?? ''
  const plan = offer?.plans?.[planName]
  const planNameHandle = React.useCallback(
    (newPlanName: string): void => {
      paymentHandle({
        planName: newPlanName,
        termInterest: 0,
        termName: '',
        termYears: 0,
      })
    },
    [paymentHandle],
  )

  const availTerms = React.useMemo(
    () =>
      c
        .entries(plan?.terms || offer?.terms || c.emptyObj)
        .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true })),
    [offer, plan],
  )
  const availTermsOpts = React.useMemo(
    (): c.Opts => availTerms.map(([value]) => ({ label: value, value })),
    [availTerms],
  )
  const termName = paymentData?.termName ?? ''
  const term =
    paymentMethodVal === 'cash'
      ? CASH_TERM
      : plan?.terms?.[termName] || offer?.terms?.[termName]
  const termNameHandle = React.useCallback(
    (val: string): void => {
      const _term =
        paymentMethodVal === 'cash'
          ? CASH_TERM
          : plan?.terms?.[val] || offer?.terms?.[val]
      console.debug({ plan, offer })
      paymentHandle({
        termInterest: _term!.interest,
        termName: val,
        termYears: _term!.years,
      })
    },
    [CASH_TERM, offer, paymentHandle, paymentMethodVal, plan],
  )

  //#region applied
  const userID = c.getUserID()
  const admins = useEnv<c.Dict<boolean>>('admins')
  const isAdmin = !!admins?.[userID ?? '']
  const canOverrideUsers = useEnv<c.Dict<boolean>>('canOverride')
  const canOverride = isAdmin || !!canOverrideUsers?.[userID ?? '']
  const [customer] = useCustomer(customerID)
  const { security_alarm_payout, security_contract_sign_customer } = customer

  const { rows: _secWifiCamRows, changeVal: secWifiCamRowsChange } = (() => {
    if (c.CustomerSchema['security_wifi_cameras_detail']) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useRowable<c.CamGroup>(customerID, 'security_wifi_cameras_detail')
    }
    return {
      rows: c.emptyArr as c.CamGroup[],
      changeVal: c.emptyFn as RowableUsed['changeVal'],
    }
  })()
  const secWifiCamRows = _secWifiCamRows.filter((cg) => !!cg.qty && !!cg.type)

  const { rows: _secHomeAutoRows, changeVal: secHomeAutoRowsChange } = (() => {
    if (c.CustomerSchema['security_home_automation_detail']) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useRowable<c.CamGroup>(
        customerID,
        'security_home_automation_detail',
      )
    }
    return {
      rows: c.emptyArr as c.CamGroup[],
      changeVal: c.emptyFn as RowableUsed['changeVal'],
    }
  })()
  const secHomeAutoRows = _secHomeAutoRows.filter((cg) => !!cg.qty && !!cg.type)
  const { rows: _secCamRows, changeVal: secCamRowsChange } = (() => {
    if (c.CustomerSchema['security_cams_detail']) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useRowable<c.CamGroup>(customerID, 'security_cams_detail')
    }
    return {
      rows: c.emptyArr as c.CamGroup[],
      changeVal: c.emptyFn as RowableUsed['changeVal'],
    }
  })()
  const secCamRows = _secCamRows.filter((cg) => !!cg.qty && !!cg.type)
  const { rows: _secSysRows, changeVal: secSysRowsChange } = (() => {
    if (c.CustomerSchema['security_system_detail']) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useRowable<c.SecSysGroup>(customerID, 'security_system_detail')
    }
    return {
      rows: c.emptyArr as c.SecSysGroup[],
      changeVal: c.emptyFn as RowableUsed['changeVal'],
    }
  })()
  const secSysRows = _secSysRows.filter((ssg) => !!ssg.qty && !!ssg.type)
  const { rows: _secTVRows, changeVal: secTVRowsChange } = (() => {
    if (c.CustomerSchema['security_tv_detail']) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useRowable<c.SecurityTV>(customerID, 'security_tv_detail')
    }
    return {
      rows: c.emptyArr as c.SecurityTV[],
      changeVal: c.emptyFn as RowableUsed['changeVal'],
    }
  })()
  const secTVRows = _secTVRows.filter((ssg) => !!ssg.type)

  const priceItems = React.useMemo(
    (): c.DealPriceItem[] =>
      c.efficiencyKeys
        .filter((key) => customer[key] === 'yes')
        .map((key) => ({
          key,
          cost: c.parseFloating(customer[(key + '_cost') as c.CustomerField]),
          price: c.parseFloating(
            customer[(key + '_customer_cost') as c.CustomerField],
          ),
          locked:
            !!customer.locked_price?.[key] ||
            !!paymentData?.lockedKeys?.includes(key),
        }))
        .filter(({ cost, price }) => cost > 0 || price > 0),
    [customer, paymentData?.lockedKeys],
  )
  const mixedParts = React.useMemo(
    () => c.partitionDealPricing(priceItems),
    [priceItems],
  )
  // Applied legacy snapshots retain their old meaning until explicit Unlock.
  const mixedPricing =
    mixedParts.isMixed &&
    ((!paymentIsApplied && !hasAnySignature(customer)) ||
      paymentData?.pricingMode === c.MIXED_DEAL_PRICING)
  const alarmPriceLocked =
    !!paymentData?.alarmLocked ||
    (mixedParts.alarm.length > 0 &&
      mixedParts.alarm.every(({ locked }) => locked))
  const legacyTotalCost = useTotalCost('company', customerID, false)
  const totalCostOverall = mixedPricing ? mixedParts.alarmCost : legacyTotalCost

  const STATE_TO_LEAD_FEE =
    useEnv<{ [x: string]: number }>('stateToLeadFee') || c.emptyObj
  /**
   * Total cost + fees:
   *   - Lead fee: UPS's fee
   *   - Alarm payout
   *   - Two story home fee (Nirvana)
   *
   * This amount won't be shown on UI, only `totalCostOverall` will
   */
  const totalCostPlusFees =
    totalCostOverall +
    c.parseFloating(security_alarm_payout) +
    c.leadFeeForDeal(
      c.leadFeeStampForDeal({
        key: customerID,
        rulesDate: customer.commission_rules_date,
        stampedLeadFee: customer.lead_fee_stamped,
        state: c.getCompanyState(customer.solarCompany || '') || '',
      }),
      STATE_TO_LEAD_FEE[customer.solarCompany] || 0,
    ) +
    // Must be mirrored in useTotalCost()
    (c.CODENAME === 'nirvana' &&
    c.CustomerSchema.homeStoryNumber &&
    customer.homeStoryNumber === '2'
      ? twoStoryFee
      : 0)

  const MAX_LOAN_COMMISSION = useEnv<number>('maxLoanCommission') || 0

  /**
   * The loan amount without any commission
   */
  const customGrandTotalWdfLowerBound =
    totalCostPlusFees / (1 - (term?.cost || 0))
  // `grandTotalWDF` is normally produced via division by (1 - dealerFee%)
  // (see V16) — IEEE-754 floats can't represent fees like 0.0425/0.1425
  // exactly in binary, so a mathematically-round $50 multiple (e.g. exactly
  // $6,250) can come out as 6250.000000000002. A strict `% 50 !== 0` check
  // treats that microscopic residue as "this loan was manually overridden",
  // which then makes finalCommission/grandTotalWDF recompute live from
  // whatever the lender's CURRENT term.cost is instead of trusting the
  // frozen applied snapshot — silently reopening an already-signed
  // customer's commission to any later fee correction (found live 2026-07-25
  // investigating a real Jaron/Synchrony report, B75/B76). Tolerate up to
  // 1 cent of float drift on either side of the nearest $50 multiple.
  const grandTotalWDFRemainder =
    paymentData != null ? Math.abs(paymentData.grandTotalWDF % 50) : 0
  const grandTotalWDFIsCleanMultipleOf50 =
    grandTotalWDFRemainder < 0.01 || grandTotalWDFRemainder > 49.99
  // A saved $0 total is "no price yet", not a custom price (13278747056)
  const storedTotalIsEmpty = c.storedGrandTotalIsEmpty(paymentData)
  // A deal whose loan was picked but never priced has payment data with no
  // grandTotalWDF at all. `undefined % 50` is NaN, which read as "a price
  // typed by hand" and opened Payment Method at $NaN/mo and Company Profit
  // $NaN until the rep pressed + (Samuel, Oct 18, 13278747056). Only a real
  // number can be a custom price; no total yet prices at the lowest real one,
  // the same figure + gives.
  const storedTotalIsNumber =
    paymentData != null &&
    typeof paymentData.grandTotalWDF === 'number' &&
    Number.isFinite(paymentData.grandTotalWDF)
  const customGrandTotalWdfIs =
    storedTotalIsNumber &&
    !storedTotalIsEmpty &&
    (!grandTotalWDFIsCleanMultipleOf50 ||
      paymentData.finalCommission < 0 ||
      paymentData.finalCommission > MAX_LOAN_COMMISSION)
  const customGrandTotalWdfVal = customGrandTotalWdfIs
    ? paymentData!.grandTotalWDF
    : -1
  const customGrandTotalWdfSet = React.useCallback(
    (val: number): void => {
      if (mixedPricing && (paymentIsApplied || alarmPriceLocked)) return
      if (!term) {
        paymentHandle({ grandTotalWDF: val })
        return
      }
      // Clearing the field must not save a $0 price with a negative
      // commission (13278747056): fall back to the lowest real price
      if (!(val > 0)) {
        const floor = c.floorGrandTotal(totalCostPlusFees, term.cost || 0)
        const floorSubtotal = floor * (1 - (term.cost || 0))
        paymentHandle({
          finalCommission: floorSubtotal - totalCostPlusFees,
          grandTotalWDF: floor,
          subtotal: floorSubtotal,
        })
        return
      }
      const multiplier = 1 / (1 - (term?.cost || 0))
      const newSubtotal = val / multiplier
      const newFinalCommission = newSubtotal - totalCostPlusFees
      paymentHandle({
        finalCommission: newFinalCommission,
        grandTotalWDF: val,
        subtotal: newSubtotal,
      })
    },
    [
      alarmPriceLocked,
      mixedPricing,
      paymentHandle,
      paymentIsApplied,
      term,
      totalCostPlusFees,
    ],
  )

  const customCommissionVal = storedTotalIsEmpty
    ? 0
    : paymentData?.finalCommission ?? 0

  const customCommissionHandle = React.useCallback(
    (newCommission: number) => {
      if (paymentIsApplied || !term || (mixedPricing && alarmPriceLocked))
        return

      const multiplier = 1 / (1 - (term?.cost || 0))

      const newSubtotal = totalCostPlusFees + newCommission
      const newGrandTotalWDF = newSubtotal * multiplier

      const roundedGrandTotal =
        Math.round((newGrandTotalWDF + Number.EPSILON) / 50) * 50

      const adjustmentTotal = roundedGrandTotal - newGrandTotalWDF

      // convert total adjustment back into commission adjustment
      const adjustment = adjustmentTotal / multiplier

      const newCommissionAdjusted = newCommission + adjustment
      const newSubtotalAdjusted = totalCostPlusFees + newCommissionAdjusted

      paymentHandle({
        finalCommission: newCommissionAdjusted,
        grandTotalWDF: roundedGrandTotal,
        subtotal: newSubtotalAdjusted,
      })
    },
    [
      alarmPriceLocked,
      mixedPricing,
      paymentHandle,
      paymentIsApplied,
      term,
      totalCostPlusFees,
    ],
  )

  const frozenMixed = mixedPricing && paymentIsApplied
  const finalCommission = frozenMixed
    ? paymentData?.finalCommission ?? 0
    : mixedPricing && alarmPriceLocked
    ? mixedParts.alarmPrice * (1 - (term?.cost || 0)) - totalCostPlusFees
    : customGrandTotalWdfIs
    ? (() => {
        if (!term) return 0
        const multiplier = 1 / (1 - (term?.cost || 0))
        const subtotal = customGrandTotalWdfVal / multiplier
        return subtotal - totalCostPlusFees
      })()
    : c.parseFloating(customCommissionVal)

  const subtotal = totalCostPlusFees + finalCommission
  const grandTotalWDF = frozenMixed
    ? paymentData?.grandTotalWDF ?? 0
    : mixedPricing && alarmPriceLocked
    ? mixedParts.alarmPrice
    : customGrandTotalWdfIs
    ? customGrandTotalWdfVal
    : subtotal / (1 - (term?.cost || 0))

  const mixedAlarmAllocation = React.useMemo(
    () => c.allocateMixedAlarm(priceItems, grandTotalWDF, alarmPriceLocked),
    [alarmPriceLocked, grandTotalWDF, priceItems],
  )
  const appliedLoan = paymentIsApplied ? paymentData : null

  const filledEfficiencies = React.useMemo(
    (): c.CustomerFields => c.getFilledEfficiencies(customer),
    [customer],
  )

  /**
   * Update customer cost and individual prices to reflect commission
   */
  const appliedLoanApply = React.useCallback((): void => {
    if (mixedPricing) {
      if (paymentIsApplied || !mixedAlarmAllocation.valid) return
      const newData: Record<string, string> = {}
      const rowsByCategory: c.DictS<
        c.CamGroups | c.SecSysGroups | c.SecurityTVs
      > = {
        security_cams: secCamRows,
        security_wifi_cameras: secWifiCamRows,
        security_home_automation: secHomeAutoRows,
        security_system: secSysRows,
        security_tv: secTVRows,
      }
      for (const item of mixedParts.alarm) {
        if (alarmPriceLocked || item.locked) continue
        const price = mixedAlarmAllocation.prices[item.key]!
        newData[item.key + '_customer_cost'] = price.toFixed(4)
        const rows = rowsByCategory[item.key]
        if (rows?.length) {
          newData[item.key + '_detail'] = c.applyLoanToSecurity(rows, price)
        }
      }
      paymentHandle({
        finalCommission,
        grandTotalWDF,
        subtotal,
        totalCost: totalCostOverall,
        pricingMode: c.MIXED_DEAL_PRICING,
        lockedKeys: Array.from(
          new Set([
            ...(paymentData?.lockedKeys || []),
            ...mixedParts.homeImprovement.map(({ key }) => key),
          ]),
        ),
      })
      // Same order as the legacy Apply below: mark the alarm prices as touched
      // first. In one combined update the store sees the `_detail` change while
      // the price is still linked and overwrites `_customer_cost` with the
      // company cost (caught live, Oct 21).
      c.updateCustomer(customerID, {
        has_touched_customer_cost: Object.fromEntries(
          mixedParts.alarm.map(({ key }) => [key, true]),
        ),
      })
      paymentIsAppliedSet(true)
      setTimeout(() => {
        c.updateCustomer(customerID, newData)
      }, 1000)
      return
    }
    paymentHandle({
      finalCommission,
      grandTotalWDF,
      subtotal,
      totalCost: totalCostOverall,
    })
    paymentIsAppliedSet(true)

    c.updateCustomer(customerID, {
      has_touched_customer_cost: Object.fromEntries(
        filledEfficiencies.map((eff) => [eff, true]),
      ),
    })

    const percents = filledEfficiencies.map((eff) => {
      const cost = c.parseFloating(customer[(eff + '_cost') as c.CustomerField])
      return cost / totalCostOverall
    })
    const grandTotals = percents.map((perc) => grandTotalWDF * perc)

    const newData = {
      ...Object.fromEntries(
        filledEfficiencies.map((eff, i) => [
          (eff + '_customer_cost') as c.CustomerField,
          grandTotals[i]!.toFixed(4),
        ]),
      ),
    }

    if (
      c.CustomerSchema['security_cams_detail'] &&
      customer.security_cams === 'yes'
    ) {
      const totalCostCams = secCamRows.reduce(
        (acc, row) => acc + row.price * row.qty,
        0,
      )
      const percentCams = totalCostCams / totalCostOverall
      const grandTotalCams = grandTotalWDF * percentCams
      newData['security_cams_detail'] = c.applyLoanToSecurity(
        secCamRows,
        grandTotalCams,
      )
      console.debug('totalCostCams', totalCostCams)
      console.debug('secCamRows', secCamRows)
      console.debug('newData', JSON.parse(newData['security_cams_detail']))
    }
    if (
      c.CustomerSchema['security_wifi_cameras_detail'] &&
      customer.security_wifi_cameras === 'yes'
    ) {
      const totalCostWifiCams = secWifiCamRows.reduce(
        (acc, row) => acc + row.price * row.qty,
        0,
      )
      const percentWifiCams = totalCostWifiCams / totalCostOverall
      const grandTotalWifiCams = grandTotalWDF * percentWifiCams
      newData['security_wifi_cameras_detail'] = c.applyLoanToSecurity(
        secWifiCamRows,
        grandTotalWifiCams,
      )
    }
    if (
      c.CustomerSchema['security_home_automation_detail'] &&
      customer.security_home_automation === 'yes'
    ) {
      const totalCostHomeAuto = secHomeAutoRows.reduce(
        (acc, row) => acc + row.price * row.qty,
        0,
      )
      const percentHomeAuto = totalCostHomeAuto / totalCostOverall
      const grandTotalHomeAuto = grandTotalWDF * percentHomeAuto
      newData['security_home_automation_detail'] = c.applyLoanToSecurity(
        secHomeAutoRows,
        grandTotalHomeAuto,
      )
    }
    if (
      c.CustomerSchema['security_system_detail'] &&
      customer.security_system === 'yes'
    ) {
      const totalCostSys = secSysRows.reduce(
        (acc, row) => acc + row.price * row.qty,
        0,
      )
      const percentSys = totalCostSys / totalCostOverall
      const grandTotalSys = grandTotalWDF * percentSys
      newData['security_system_detail'] = c.applyLoanToSecurity(
        secSysRows,
        grandTotalSys,
      )
    }
    if (
      c.CustomerSchema['security_tv_detail'] &&
      customer.security_tv === 'yes'
    ) {
      const totalCostTV = secTVRows.reduce((acc, row) => acc + row.price, 0)
      const percentTV = totalCostTV / totalCostOverall
      const grandTotalTV = grandTotalWDF * percentTV
      newData['security_tv_detail'] = c.applyLoanToSecurity(
        secTVRows,
        grandTotalTV,
      )
    }

    setTimeout(() => {
      c.updateCustomer(customerID, newData)
    }, 1000) // TODO
  }, [
    customer,
    customerID,
    filledEfficiencies,
    finalCommission,
    grandTotalWDF,
    paymentHandle,
    paymentIsAppliedSet,
    secCamRows,
    secHomeAutoRows,
    secSysRows,
    secTVRows,
    secWifiCamRows,
    subtotal,
    totalCostOverall,
    alarmPriceLocked,
    mixedAlarmAllocation,
    mixedParts,
    mixedPricing,
    paymentData?.lockedKeys,
    paymentIsApplied,
  ])

  const appliedLoanUnlock = React.useCallback(() => {
    if (!!security_contract_sign_customer && !canOverride) {
      return
    }
    const shouldResetSecurity = (key: string) =>
      !mixedParts.isMixed ||
      (!alarmPriceLocked &&
        !mixedParts.alarm.find((item) => item.key === key)?.locked)
    if (
      shouldResetSecurity('security_cams') &&
      c.CustomerSchema['security_cams_detail'] &&
      customer.security_cams === 'yes'
    ) {
      for (const camRow of secCamRows) {
        // Trigger a change so the original price gets put back in
        secCamRowsChange('qty', camRow.id, camRow.qty)
      }
    }
    if (
      shouldResetSecurity('security_wifi_cameras') &&
      c.CustomerSchema['security_wifi_cameras_detail'] &&
      customer.security_wifi_cameras === 'yes'
    ) {
      for (const camRow of secWifiCamRows) {
        // Trigger a change so the original price gets put back in
        secWifiCamRowsChange('qty', camRow.id, camRow.qty)
      }
    }
    if (
      shouldResetSecurity('security_home_automation') &&
      c.CustomerSchema['security_home_automation_detail'] &&
      customer.security_home_automation === 'yes'
    ) {
      for (const autoRow of secHomeAutoRows) {
        // Trigger a change so the original price gets put back in
        secHomeAutoRowsChange('qty', autoRow.id, autoRow.qty)
      }
    }
    if (
      shouldResetSecurity('security_system') &&
      c.CustomerSchema['security_system_detail'] &&
      customer.security_system === 'yes'
    ) {
      for (const sysRow of secSysRows) {
        // Trigger a change so the original price gets put back in
        secSysRowsChange('qty', sysRow.id, sysRow.qty)
      }
    }
    if (
      shouldResetSecurity('security_tv') &&
      c.CustomerSchema['security_tv_detail'] &&
      customer.security_tv === 'yes'
    ) {
      for (const tvRow of secTVRows) {
        // Trigger a change so the original price gets put back in
        secTVRowsChange('type', tvRow.id, tvRow.type)
      }
    }

    // Unlock explicitly transitions an applied legacy mixed deal: preserve
    // HI prices and start the alarm at its existing category sum.
    if (mixedParts.isMixed) {
      const alarmSubtotal = mixedParts.alarmPrice * (1 - (term?.cost || 0))
      paymentHandle({
        pricingMode: c.MIXED_DEAL_PRICING,
        grandTotalWDF: mixedParts.alarmPrice,
        subtotal: alarmSubtotal,
        totalCost: mixedParts.alarmCost,
        finalCommission:
          alarmSubtotal -
          mixedParts.alarmCost -
          (totalCostPlusFees - totalCostOverall),
      })
    }
    c.updateCustomer(customerID, {
      has_touched_customer_cost: Object.fromEntries(
        (mixedParts.isMixed
          ? mixedParts.alarm.map(({ key }) => key)
          : filledEfficiencies
        ).map((eff) => [eff, mixedParts.isMixed && !shouldResetSecurity(eff)]),
      ),
    })
    paymentIsAppliedSet(false)
  }, [
    alarmPriceLocked,
    mixedParts,
    paymentHandle,
    term,
    totalCostPlusFees,
    totalCostOverall,
    canOverride,
    customer.security_cams,
    customer.security_home_automation,
    customer.security_system,
    customer.security_tv,
    customer.security_wifi_cameras,
    customerID,
    filledEfficiencies,
    secCamRows,
    secCamRowsChange,
    secHomeAutoRows,
    secHomeAutoRowsChange,
    secSysRows,
    secSysRowsChange,
    secTVRows,
    secTVRowsChange,
    secWifiCamRows,
    secWifiCamRowsChange,
    paymentIsAppliedSet,
    security_contract_sign_customer,
  ])
  //#endregion

  return {
    mixedPricing,
    mixedAlarmPriceLocked: mixedPricing && alarmPriceLocked,
    mixedAlarmPrices: mixedAlarmAllocation.prices,
    mixedPricingValid: !mixedPricing || mixedAlarmAllocation.valid,
    mixedHomeImprovementPrice: mixedParts.homeImprovementPrice,
    mixedContractTotal: mixedParts.homeImprovementPrice + grandTotalWDF,
    appliedLoan,
    appliedLoanApply,
    appliedLoanCustomGrandTotalWdfLowerBound: customGrandTotalWdfLowerBound,
    appliedLoanFinalCommission: finalCommission,
    appliedLoanFinalCommissionHandle: customCommissionHandle,
    appliedLoanGrandTotalWDF: paymentIsApplied
      ? paymentData?.grandTotalWDF ?? grandTotalWDF
      : grandTotalWDF,
    appliedLoanGrandTotalWDFHandle: customGrandTotalWdfSet,
    appliedLoanNoDataInputted: totalCostOverall === 0,
    appliedLoanTotalCostOverall: totalCostOverall,
    appliedLoanUnlock,

    /**
     * Raw calculated loan data, regardless of whether it's been officially
     * applied (`paymentIsApplied`) — `null` if no loan has ever been
     * calculated for this customer. Contract templates and the PDF export
     * must keep using `appliedLoan` (customer-facing, only the officially
     * applied/locked loan). Internal-only views (e.g. Job Summary,
     * `!isPDF && !readOnly`) can use this to show an "Estimated Monthly
     * Payment" before the rep has locked it in via Payment Method.
     */
    paymentData,

    availLoanCompanies,
    availLoanCompaniesNames,
    availLoanCompaniesOpts,
    loanCompany,
    loanCompanyName,
    loanCompanyNameHandle,

    availOffers,
    availOffersOpts,
    offer,
    offerName,
    offerNameHandle,

    availPlans,
    availPlansOpts,
    plan,
    planName,
    planNameHandle,

    availTerms,
    availTermsOpts,
    term,
    termName,
    termNameHandle,

    paymentMethodSet,
    paymentMethodVal,
  } as const
}

/**
 * The Documentation-tab sections this customer should see (Monday 12803784158).
 *
 * The decision itself lives in `shouldShowCategory`, which is dependency-free
 * and unit tested. This hook only gathers what that rule needs: the customer's
 * products, and whether each section already holds media.
 *
 * `hasMedia` is the part that matters most. Without it, a customer switched to
 * alarms-only loses every route to Assessment photos already taken - they
 * cannot be viewed, re-noted or deleted, because filtering this list does not
 * filter the underlying data. That is what got the Aug 14 attempt reverted.
 *
 * RETURN SHAPE IS LOAD-BEARING: `src/web/routes/customer/Customer.tsx`
 * destructures `[category, mediaKinds]` from this. Kept identical on purpose.
 * The web does now hide irrelevant sections too, which is a deliberate
 * consequence rather than an accidental one - it shows the same sections for
 * the same customer, and Jaron's instruction was about the sections, not about
 * one platform.
 */
export const useCategoryToMediaKinds = (customerID: string) => {
  const { currProducts } = useProducts(customerID)
  const selectCategoriesWithMedia = React.useMemo(
    () => c.makeSelectCategoriesWithMedia(),
    [],
  )
  const categoriesWithMedia = c.useSelector((state) =>
    selectCategoriesWithMedia(state, { customerID }),
  )

  return c
    .entries(c.categoryToMediaKinds)
    .filter(([category]) =>
      c.shouldShowCategory(
        category,
        currProducts,
        categoriesWithMedia[category] ?? false,
        c.CODENAME === 'nirvana',
      ),
    )
}

//#region security
// 13019086326, Jaron 2026-09-09/10: "customer has choice between 2 3 4 6 8" -
// 2 and 3 are the first slots of the existing 4-camera set, not new ones.
const securityCamsMediaKinds2: c.MediaKind[] = [
  'security_cam0',
  'security_cam1',
]
const securityCamsMediaKinds3: c.MediaKind[] = [
  ...securityCamsMediaKinds2,
  'security_cam2',
]
const securityCamsMediaKinds4: c.MediaKind[] = [
  'security_cam0',
  'security_cam1',
  'security_cam2',
  'security_cam3',
]
const securityCamsMediaKinds6: c.MediaKind[] = [
  ...securityCamsMediaKinds4,
  'security_cam4',
  'security_cam5',
]
const securityCamsMediaKinds8: c.MediaKind[] = [
  ...securityCamsMediaKinds6,
  'security_cam6',
  'security_cam7',
]
/**
 * `security_cams_is_6` predates the 8-camera tier and was a YesNo field —
 * 'yes' meant 6 cameras, 'no'/empty meant 4. Existing customer records still
 * hold those values; '8' only appears going forward. Keep interpreting the
 * old values the same way so nothing already saved changes meaning.
 */
export const useSecurityCamAngles = (
  customerID: string,
): readonly c.MediaKind[] => {
  // TODO: Why isn't useCustomerField here working correctly?
  const [{ security_cams_is_6: camsIs6 }] = useCustomer(customerID)

  if (camsIs6 === '8') return securityCamsMediaKinds8
  if (camsIs6 === '6' || camsIs6 === 'yes') return securityCamsMediaKinds6
  if (camsIs6 === '3') return securityCamsMediaKinds3
  if (camsIs6 === '2') return securityCamsMediaKinds2
  return securityCamsMediaKinds4
}
//#endregion security

export type DetailLine = c.r<{
  id: string
  name: string
  notes: string[]
  price: number
  qty: number
}>

const dashes = '-----------------------------------------------'

export const useDetailLines = (customerID: string) => {
  //#region global
  const [customer] = useCustomer(customerID)
  const {
    security_cams,
    security_cams_show,
    security_system,
    security_system_show,
    security_tv,
    security_tv_show,
    security_wifi_cameras,
    security_wifi_cameras_show,
    security_home_automation,
    security_home_automation_show,
  } = customer
  const { rows: _secCamRows } = useRowable<c.CamGroup>(
    customerID,
    'security_cams_detail',
  )
  const { rows: _secSysRows } = useRowable<c.SecSysGroup>(
    customerID,
    'security_system_detail',
  )
  const { rows: _secTVRows } = useRowable<c.SecurityTV>(
    customerID,
    'security_tv_detail',
  )
  const { rows: _secWifiCamRows } = useRowable<c.CamGroup>(
    customerID,
    'security_wifi_cameras_detail',
  )
  const { rows: _secHomeAutoRows } = useRowable<c.CamGroup>(
    customerID,
    'security_home_automation_detail',
  )
  //#endregion global
  //#region local
  // Reverted 2026-07-30 (Jaron, Slack #Hilo, live on Leslie Jacobs' MSE
  // contract): "Why are they not itemized" — B99's 2026-06-23 "notes off by
  // default" request is now the opposite of what Jaron wants live. Back to
  // not-collapsed by default, matching pre-B99 behavior (`{}`, no entries
  // forced true). Samuel already agreed to this exact revert in Slack but
  // hadn't committed it yet.
  const [collapsed, collapsedSet] = React.useState<c.DictS<boolean>>({})
  const collapsedHandleMax = React.useMemo<c.DictS<() => void>>(
    () =>
      c.mapValues(c.efficiencyToInputs, (_, efficiencyKey) => () => {
        collapsedSet((_) => ({ ..._, [efficiencyKey]: false }))
      }),
    [],
  )
  const collapsedHandleMin = React.useMemo<c.DictS<() => void>>(
    () =>
      c.mapValues(c.efficiencyToInputs, (_, efficiencyKey) => () => {
        collapsedSet((_) => ({ ..._, [efficiencyKey]: true }))
      }),
    [],
  )

  //#endregion local

  const nonSecurityActiveEfficiencies = React.useMemo(
    () =>
      c.efficiencyKeys
        .filter((k) => !k.startsWith('security_'))
        .filter((k) => !k.startsWith('custom_'))
        .filter((k) => customer[k] === 'yes')
        .map((field): DetailLine => {
          const notes = c.customerNotes(customer, field)

          // NOTE: reads `<field>extra_notes`, with no underscore, so the key
          // never exists and this is always ''. Left as-is deliberately: no
          // customer with a current contract company has extra notes on a
          // non-security efficiency (measured across all 7,151), and the ones
          // that do carry rep commission arithmetic - "I added $3000 for my
          // extra commission" - which correcting the key would start printing
          // onto customer-facing documents. That is Jaron's call, not a typo
          // fix.
          const extraNotes =
            (
              customer[(field + 'extra_notes') as c.CustomerField] as string
            )?.trim() || ''

          const notesLines = notes.length === 0 ? [] : notes.split('\n')
          const extraNotesLines =
            extraNotes.length === 0 ? [] : extraNotes.split('\n')

          const qty = customer[(field + '_qty') as c.CustomerField] as number

          return {
            id: field,
            name: c.getFieldLabel(field),
            notes: [...notesLines, ...extraNotesLines],
            price: c.parseFloating(
              customer[(field + '_customer_cost') as c.CustomerField],
            ),
            qty: qty || -1,
          }
        }),
    [customer],
  )

  const secCamRows =
    security_cams === 'no' || security_cams_show === 0
      ? (c.EMPTY_ARRAY as c.CamGroup[])
      : _secCamRows.filter((cg) => !!cg.qty && !!cg.type)

  const secSysRows =
    security_system === 'no' || security_system_show === 0
      ? (c.EMPTY_ARRAY as c.SecSysGroup[])
      : _secSysRows.filter((ssg) => !!ssg.qty && !!ssg.type)

  const secTVRows =
    security_tv === 'no' || security_tv_show === 0
      ? (c.EMPTY_ARRAY as c.SecurityTV[])
      : _secTVRows.filter((ssg) => !!ssg.type)

  const secWifiCamRows =
    security_wifi_cameras === 'no' || security_wifi_cameras_show === 0
      ? (c.EMPTY_ARRAY as c.CamGroup[])
      : _secWifiCamRows.filter((cg) => !!cg.qty && !!cg.type)

  const secHomeAutoRows =
    security_home_automation === 'no' || security_home_automation_show === 0
      ? (c.EMPTY_ARRAY as c.CamGroup[])
      : _secHomeAutoRows.filter((cg) => !!cg.qty && !!cg.type)

  const customItems = c
    .getFilledCustomFields(customer)
    .map((customField): DetailLine => {
      const qty = customer[(customField + '_qty') as c.CustomerField] as number

      const customerCost = customer[
        (customField + '_customer_cost') as c.CustomerField
      ] as string

      const notes = c.customerNotes(customer, customField)

      return {
        id: customField,
        name: customer[(customField + '_name') as c.CustomerField] as string,
        notes: notes.length === 0 ? [] : notes.split('\n'),
        price: c.parseFloating(customerCost) / qty,
        qty: qty,
      }
    })

  const detailLines = React.useMemo(
    (): readonly DetailLine[] => [
      ...customItems,
      ...nonSecurityActiveEfficiencies,
      //#region securityCamsItems
      ...(customer.security_cams === 'no' || secCamRows.length === 0
        ? []
        : [
            {
              id: 'security_cams',
              name: collapsed['security_cams']
                ? c.getFieldLabel('security_cams')
                : c.scopeSectionHeader('security_cams'),
              notes: [],
              price: collapsed['security_cams']
                ? c.parseFloating(customer.security_cams_customer_cost)
                : -1,
              qty: -1,
            },
            // The five security categories below are sanitised here rather than by
            // swapping this hook for `useSecurityScopeDetailLines`: that one also
            // drops `security_system` entirely, plus efficiencies and custom fields
            // (T56), which on these contracts would delete line items the customer
            // actually bought. Only the rendered text is made alarm-safe.
            //
            // Doing it at the source instead of in each contract is V62: five
            // consumers render these same rows (FirstElectricContract x4,
            // WildcatContract, CertOfCompletion's non-CE path) and only one of them
            // was ever given the switch. B132 was the same shape one surface earlier.
            //
            // Efficiencies are deliberately NOT filtered: `stripAlarmMentions` drops
            // lines containing "solar", right for a camera whose name carries it and
            // ruinous for a real Solar job.
          ]),
      ...(customer.security_cams === 'no' || collapsed['security_cams']
        ? []
        : secCamRows
      ).map(
        ({ id, notes, price, qty, type }): DetailLine => ({
          id,
          name: c.resolveAlarmSafeProductName(type),
          notes: [c.stripAlarmMentions(notes)],
          price,
          qty,
        }),
      ),
      ...(customer.security_cams === 'no' ||
      secCamRows.length === 0 ||
      customer.security_cams_extra_notes.trim().length === 0 ||
      collapsed['security_cams']
        ? []
        : [
            {
              id: 'security_cams_extra_notes',
              name: dashes,
              notes: customer.security_cams_extra_notes.trim().split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_cams_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityCamsItems
      //#region securitySystemItems
      ...(customer.security_system === 'no' || secSysRows.length === 0
        ? []
        : [
            {
              id: 'security_system',
              name: collapsed['security_system']
                ? c.getFieldLabel('security_system')
                : c.scopeSectionHeader('security_system'),
              notes: [],
              price: collapsed['security_system']
                ? c.parseFloating(customer.security_system_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_system === 'no' || collapsed['security_system']
        ? []
        : secSysRows
      ).map(
        ({ id, notes, price, qty, type }): DetailLine => ({
          id,
          name: c.resolveAlarmSafeProductName(type),
          notes: [c.stripAlarmMentions(notes)],
          price,
          qty,
        }),
      ),
      ...(customer.security_system === 'no' ||
      secSysRows.length === 0 ||
      customer.security_system_extra_notes.trim().length === 0 ||
      collapsed['security_system']
        ? []
        : [
            {
              id: 'security_system_extra_notes',
              name: dashes,
              notes: customer.security_system_extra_notes.trim().split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_system_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securitySystemItems
      //#region securityTvItems
      ...(customer.security_tv === 'no' || secTVRows.length === 0
        ? []
        : [
            {
              id: 'security_tv',
              name: collapsed['security_tv']
                ? c.getFieldLabel('security_tv')
                : c.scopeSectionHeader('security_tv'),
              notes: [],
              price: collapsed['security_tv']
                ? c.parseFloating(customer.security_tv_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_tv === 'no' || collapsed['security_tv']
        ? []
        : secTVRows
      ).map(
        ({ id, price, qty, type }): DetailLine => ({
          id,
          name: c.resolveAlarmSafeProductName(type),
          notes: [],
          price,
          qty,
        }),
      ),
      ...(customer.security_tv === 'no' ||
      secTVRows.length === 0 ||
      customer.security_tv_extra_notes.trim().length === 0 ||
      collapsed['security_tv']
        ? []
        : [
            {
              id: 'security_tv_extra_notes',
              name: dashes,
              notes: customer.security_tv_extra_notes.trim().split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_tv_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityTvItems
      //#region securityWifiCamerasItems
      ...(customer.security_wifi_cameras === 'no' || secWifiCamRows.length === 0
        ? []
        : [
            {
              id: 'security_wifi_cameras',
              name: collapsed['security_wifi_cameras']
                ? c.getFieldLabel('security_wifi_cameras')
                : c.scopeSectionHeader('security_wifi_cameras'),
              notes: [],
              price: collapsed['security_wifi_cameras']
                ? c.parseFloating(customer.security_wifi_cameras_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_wifi_cameras === 'no' ||
      collapsed['security_wifi_cameras']
        ? []
        : secWifiCamRows
      ).map(
        ({ id, notes, price, qty, type }): DetailLine => ({
          id,
          name: c.resolveAlarmSafeProductName(type),
          notes: [c.stripAlarmMentions(notes)],
          price,
          qty,
        }),
      ),
      ...(customer.security_wifi_cameras === 'no' ||
      secWifiCamRows.length === 0 ||
      customer.security_wifi_cameras_extra_notes.trim().length === 0 ||
      collapsed['security_wifi_cameras']
        ? []
        : [
            {
              id: 'security_wifi_cameras_extra_notes',
              name: dashes,
              notes: customer.security_wifi_cameras_extra_notes
                .trim()
                .split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_wifi_cameras_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityWifiCamerasItems
      //#region securityHomeAutomationItems
      ...(customer.security_home_automation === 'no' ||
      secHomeAutoRows.length === 0
        ? []
        : [
            {
              id: 'security_home_automation',
              name: collapsed['security_home_automation']
                ? c.getFieldLabel('security_home_automation')
                : c.scopeSectionHeader('security_home_automation'),
              notes: [],
              price: collapsed['security_home_automation']
                ? c.parseFloating(
                    customer.security_home_automation_customer_cost,
                  )
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_home_automation === 'no' ||
      collapsed['security_home_automation']
        ? []
        : secHomeAutoRows
      ).map(
        ({ id, notes, price, qty, type }): DetailLine => ({
          id,
          name: c.resolveAlarmSafeProductName(type),
          notes: [c.stripAlarmMentions(notes)],
          price,
          qty,
        }),
      ),
      ...(customer.security_home_automation === 'no' ||
      secHomeAutoRows.length === 0 ||
      customer.security_home_automation_extra_notes.trim().length === 0 ||
      collapsed['security_home_automation']
        ? []
        : [
            {
              id: 'security_home_automation_extra_notes',
              name: dashes,
              notes: customer.security_home_automation_extra_notes
                .trim()
                .split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_home_automation_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityHomeAutomationItems
    ],
    [
      collapsed,
      customItems,
      customer.security_cams,
      customer.security_cams_customer_cost,
      customer.security_cams_extra_notes,
      customer.security_home_automation,
      customer.security_home_automation_customer_cost,
      customer.security_home_automation_extra_notes,
      customer.security_system,
      customer.security_system_customer_cost,
      customer.security_system_extra_notes,
      customer.security_tv,
      customer.security_tv_customer_cost,
      customer.security_tv_extra_notes,
      customer.security_wifi_cameras,
      customer.security_wifi_cameras_customer_cost,
      customer.security_wifi_cameras_extra_notes,
      nonSecurityActiveEfficiencies,
      secCamRows,
      secHomeAutoRows,
      secSysRows,
      secTVRows,
      secWifiCamRows,
    ],
  )

  return {
    collapsed,
    collapsedHandleMax,
    collapsedHandleMin,
    detailLines,
  }
}

/**
 * Excluding the `security_system` category isn't enough by itself — some
 * KEPT categories' auto-generated notes (`securityAutoNotes.ts`) still
 * describe alarm-system functionality in passing, not just the literal word
 * "alarm". Found live 2026-07-23 (Ruby Caluag, then again from Jaron
 * directly, circling the exact bullets: "Notes cannot talk about alarm
 * stuff like sensors etc"): Eufy HomeBase 3 (lives in
 * `security_wifi_cameras`/`security_cams`) describes itself as an alarm
 * hub — "Central control hub for security system", "Arm / Disarm modes",
 * "Built-in siren", "Connects to door/window sensors, motion detectors,
 * smoke & CO detectors" — none of which say "alarm" literally.
 * Keyword list (checked against all 25 `securityAutoNotes.ts` entries,
 * `alarm|siren|sensors?|detectors?|arm/disarm|"security system"` catches
 * exactly the 4 kept-category products with a real alarm-adjacent bullet —
 * HomeBase 3, Solar S4 Camera/Wireless Cam, S4 Max Camera (PoE), Smart
 * Display's "security system" line — and nothing else. Deliberately does
 * NOT match "detection" (only "detector(s)"), so camera AI features like
 * "Motion detection alerts"/"Human / vehicle / animal detection" survive
 * untouched — those describe the camera itself, not an alarm system.
 * Strips any line (split on `\n`) matching, per-line not whole-note, so
 * the rest of a product's bullets still render.
 */
// Also strips "solar" mentions (2026-07-30, Luis — CE only ever sells
// security/camera equipment, not solar, per B89; the alarm-mention filter
// already lives on this same CE-restricted display path so solar wording
// piggybacks on it rather than a separate pass) — e.g. "Integrated solar
// charging panel", "Solar-assisted charging", "Solar-compatible", "Integrated
// solar panel for continuous power". Raw `securityAutoNotes.ts` is untouched;
// this only filters what CE's Scope of Work actually renders.
// Moved to `common/helpers/alarmSafeNotes.ts` so the Job Summary can use the
// same implementation instead of rendering these notes unfiltered — see B132.
// Kept as local aliases so the call sites below read unchanged.
const stripAlarmMentions = c.stripAlarmMentions
const resolveAlarmSafeProductName = c.resolveAlarmSafeProductName

/**
 * Same shape/pattern as `useDetailLines`, restricted to the 4 non-alarm
 * security categories (`security_cams`, `security_wifi_cameras`,
 * `security_home_automation`, `security_tv`) — deliberately excludes
 * `security_system` (alarm panel/sensors/keypad/etc, product `type` values
 * literally include the word "alarm", e.g. `'Alarm panel'`, `'Smoke alarm'`)
 * plus non-security efficiencies and custom fields. For the CE
 * (HomeImprovementAgreement) contract's Scope of Work section, which per
 * Maria G/Jaron must only ever mention cameras/doorbell/home
 * automation/homebase — never alarm-related products (see T56). Also see
 * `stripAlarmMentions` — category exclusion alone doesn't catch every
 * "alarm" mention, some survive inside a kept category's note text.
 */
export const useSecurityScopeDetailLines = (customerID: string) => {
  //#region global
  const [customer] = useCustomer(customerID)
  const {
    security_cams,
    security_cams_show,
    security_tv,
    security_tv_show,
    security_wifi_cameras,
    security_wifi_cameras_show,
    security_home_automation,
    security_home_automation_show,
  } = customer
  const { rows: _secCamRows } = useRowable<c.CamGroup>(
    customerID,
    'security_cams_detail',
  )
  const { rows: _secTVRows } = useRowable<c.SecurityTV>(
    customerID,
    'security_tv_detail',
  )
  const { rows: _secWifiCamRows } = useRowable<c.CamGroup>(
    customerID,
    'security_wifi_cameras_detail',
  )
  const { rows: _secHomeAutoRows } = useRowable<c.CamGroup>(
    customerID,
    'security_home_automation_detail',
  )
  //#endregion global
  //#region local
  // Reverted 2026-07-30, same reason/instance as `useDetailLines` above —
  // kept in sync so CE's Scope of Work doesn't silently stay collapsed
  // while every other company's expanded.
  const [collapsed, collapsedSet] = React.useState<c.DictS<boolean>>({})
  const collapsedHandleMax = React.useMemo<c.DictS<() => void>>(
    () =>
      c.mapValues(c.efficiencyToInputs, (_, efficiencyKey) => () => {
        collapsedSet((_) => ({ ..._, [efficiencyKey]: false }))
      }),
    [],
  )
  const collapsedHandleMin = React.useMemo<c.DictS<() => void>>(
    () =>
      c.mapValues(c.efficiencyToInputs, (_, efficiencyKey) => () => {
        collapsedSet((_) => ({ ..._, [efficiencyKey]: true }))
      }),
    [],
  )
  //#endregion local

  const secCamRows =
    security_cams === 'no' || security_cams_show === 0
      ? (c.EMPTY_ARRAY as c.CamGroup[])
      : _secCamRows.filter((cg) => !!cg.qty && !!cg.type)

  const secTVRows =
    security_tv === 'no' || security_tv_show === 0
      ? (c.EMPTY_ARRAY as c.SecurityTV[])
      : _secTVRows.filter((ssg) => !!ssg.type)

  const secWifiCamRows =
    security_wifi_cameras === 'no' || security_wifi_cameras_show === 0
      ? (c.EMPTY_ARRAY as c.CamGroup[])
      : _secWifiCamRows.filter((cg) => !!cg.qty && !!cg.type)

  const secHomeAutoRows =
    security_home_automation === 'no' || security_home_automation_show === 0
      ? (c.EMPTY_ARRAY as c.CamGroup[])
      : _secHomeAutoRows.filter((cg) => !!cg.qty && !!cg.type)

  const detailLines = React.useMemo(
    (): readonly DetailLine[] => [
      //#region securityCamsItems
      ...(customer.security_cams === 'no' || secCamRows.length === 0
        ? []
        : [
            {
              id: 'security_cams',
              name: collapsed['security_cams']
                ? c.getFieldLabel('security_cams')
                : c.scopeSectionHeader('security_cams'),
              notes: [],
              price: collapsed['security_cams']
                ? c.parseFloating(customer.security_cams_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_cams === 'no' || collapsed['security_cams']
        ? []
        : secCamRows
      ).map(
        ({ id, notes, price, qty, type }): DetailLine => ({
          id,
          name: type,
          notes: [stripAlarmMentions(notes)],
          price,
          qty,
        }),
      ),
      ...(customer.security_cams === 'no' ||
      secCamRows.length === 0 ||
      customer.security_cams_extra_notes.trim().length === 0 ||
      collapsed['security_cams']
        ? []
        : [
            {
              id: 'security_cams_extra_notes',
              name: dashes,
              notes: stripAlarmMentions(
                customer.security_cams_extra_notes.trim(),
              ).split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_cams_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityCamsItems
      //#region securityTvItems
      ...(customer.security_tv === 'no' || secTVRows.length === 0
        ? []
        : [
            {
              id: 'security_tv',
              name: collapsed['security_tv']
                ? c.getFieldLabel('security_tv')
                : c.scopeSectionHeader('security_tv'),
              notes: [],
              price: collapsed['security_tv']
                ? c.parseFloating(customer.security_tv_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_tv === 'no' || collapsed['security_tv']
        ? []
        : secTVRows
      ).map(
        ({ id, price, qty, type }): DetailLine => ({
          id,
          name: type,
          notes: [],
          price,
          qty,
        }),
      ),
      ...(customer.security_tv === 'no' ||
      secTVRows.length === 0 ||
      customer.security_tv_extra_notes.trim().length === 0 ||
      collapsed['security_tv']
        ? []
        : [
            {
              id: 'security_tv_extra_notes',
              name: dashes,
              notes: stripAlarmMentions(
                customer.security_tv_extra_notes.trim(),
              ).split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_tv_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityTvItems
      //#region securityWifiCamerasItems
      ...(customer.security_wifi_cameras === 'no' || secWifiCamRows.length === 0
        ? []
        : [
            {
              id: 'security_wifi_cameras',
              name: collapsed['security_wifi_cameras']
                ? c.getFieldLabel('security_wifi_cameras')
                : c.scopeSectionHeader('security_wifi_cameras'),
              notes: [],
              price: collapsed['security_wifi_cameras']
                ? c.parseFloating(customer.security_wifi_cameras_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_wifi_cameras === 'no' ||
      collapsed['security_wifi_cameras']
        ? []
        : secWifiCamRows
      ).map(
        ({ id, notes, price, qty, type }): DetailLine => ({
          id,
          name: resolveAlarmSafeProductName(type),
          notes: [stripAlarmMentions(notes)],
          price,
          qty,
        }),
      ),
      ...(customer.security_wifi_cameras === 'no' ||
      secWifiCamRows.length === 0 ||
      customer.security_wifi_cameras_extra_notes.trim().length === 0 ||
      collapsed['security_wifi_cameras']
        ? []
        : [
            {
              id: 'security_wifi_cameras_extra_notes',
              name: dashes,
              notes: stripAlarmMentions(
                customer.security_wifi_cameras_extra_notes.trim(),
              ).split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_wifi_cameras_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityWifiCamerasItems
      //#region securityHomeAutomationItems
      ...(customer.security_home_automation === 'no' ||
      secHomeAutoRows.length === 0
        ? []
        : [
            {
              id: 'security_home_automation',
              name: collapsed['security_home_automation']
                ? c.getFieldLabel('security_home_automation')
                : c.scopeSectionHeader('security_home_automation'),
              notes: [],
              price: collapsed['security_home_automation']
                ? c.parseFloating(
                    customer.security_home_automation_customer_cost,
                  )
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_home_automation === 'no' ||
      collapsed['security_home_automation']
        ? []
        : secHomeAutoRows
      ).map(
        ({ id, notes, price, qty, type }): DetailLine => ({
          id,
          name: type,
          notes: [stripAlarmMentions(notes)],
          price,
          qty,
        }),
      ),
      ...(customer.security_home_automation === 'no' ||
      secHomeAutoRows.length === 0 ||
      customer.security_home_automation_extra_notes.trim().length === 0 ||
      collapsed['security_home_automation']
        ? []
        : [
            {
              id: 'security_home_automation_extra_notes',
              name: dashes,
              notes: stripAlarmMentions(
                customer.security_home_automation_extra_notes.trim(),
              ).split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_home_automation_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityHomeAutomationItems
    ],
    [
      collapsed,
      customer.security_cams,
      customer.security_cams_customer_cost,
      customer.security_cams_extra_notes,
      customer.security_home_automation,
      customer.security_home_automation_customer_cost,
      customer.security_home_automation_extra_notes,
      customer.security_tv,
      customer.security_tv_customer_cost,
      customer.security_tv_extra_notes,
      customer.security_wifi_cameras,
      customer.security_wifi_cameras_customer_cost,
      customer.security_wifi_cameras_extra_notes,
      secCamRows,
      secHomeAutoRows,
      secTVRows,
      secWifiCamRows,
    ],
  )

  return {
    collapsed,
    collapsedHandleMax,
    collapsedHandleMin,
    detailLines,
  }
}
